import { GanttTimelineRange } from './gantt-timeline-range';
import { GanttFloatingFilters } from './gantt-floating-filters';
import { createGanttSortComparator, cycleGanttSort, normalizeGanttSort, rememberGanttSort } from './gantt-sorting';
import { escapeGanttColumnText, formatGanttColumnValue, isGanttColumnSortable } from './gantt-column-values';
import { createPlanningSave } from '@app/core/planning/planning-save';
import { PlanningEngine } from '@app/core/planning/planning-engine';
import { PlanningLoadIndex } from '@app/core/planning/planning-load';
import { PlanningConstraints, PlanningMove, PlanningSuccess } from '@app/core/planning/planning.types';
import { planningDateFromDate, planningDateToDate, planningTime } from '@app/core/planning/planning-date';
import { Component, ErrorHandler, ElementRef, ViewChild, OnInit, OnChanges, OnDestroy, ViewEncapsulation, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Subject, timer, debounceTime, map, switchMap, takeUntil } from 'rxjs';
import { Gantt, GanttConfigOptions as DhtmlxGanttConfigOptions, GanttStatic, Link, Task } from '../../../../assets/js/codebase/dhtmlxgantt';
import { GanttActionTrigger, GanttNavigationArea, GanttPlanningDirection, GanttSaveChange, GanttSaveError, GanttSaveResponse, GanttService, resolveGanttDataTaskRequest } from '@app/services/gantt.service';
import { TranslateService } from '@app/core/services/translate.service';
import { StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { ModalService } from '@app/core/services/modal.service';
import { PopupService } from '@app/core/services/popup.service';
import { RememberService } from '@app/core/services/remember.service';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { createDefaultGanttOptions, customCoerceBooleanProperty, isGanttConfigurationColumn, mergeGanttOptions, normalizeGanttOptions, toNativeGanttConfig } from './gantt-adapter';
import { GanttColumn, GanttLink, GanttOptions, GanttRememberState, GanttRememberViewState, GanttSetup, GanttSortState, GanttTask, GanttTaskChangeInfo, MachineResource, PartialGanttOptions } from './gantt.types';
import { configureGanttZoom, getCurrentGanttZoomLevel, normalizeGanttZoomLevel } from './gantt-zoom';
import { applyRememberedColumnState, createGanttRememberState, createRememberedColumnState, getColumnPropertyKey, isGanttRememberState } from './gantt-remember';

@Component({
    selector: 'tt-gantt',
    host: { class: 'tt-gantt' },
    standalone: false,
    templateUrl: './gantt.component.html',
    styleUrls: ['./gantt.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class GanttComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('gantt_here', { static: true })
    public ganttContainer!: ElementRef<HTMLElement>;

    @ViewChild('gantt_viewport', { static: true })
    private ganttViewport!: ElementRef<HTMLElement>;

    private errorToastText?: ElementRef<HTMLElement>;

    @ViewChild('errorToastText')
    private set errorToastTextElement(element: ElementRef<HTMLElement> | undefined) {
        this.errorToastText = element;
        this.queueErrorToastMeasure();
    }

    @Output()
    public ttReady = new EventEmitter<GanttStatic>();

    private rememberId?: string;

    @Input()
    public get ttRememberId(): string {
        return this.rememberId ?? this.ttOptions?.rememberId ?? '';
    }
    public set ttRememberId(value: string) {
        this.rememberId = value;
    }

    @Input()
    public ttOptions?: PartialGanttOptions;

    @Output()
    public ttOptionsChange = new EventEmitter<GanttOptions>();

    private criticalPath = false;

    private multiSelect = false;

    private linksEditable = false;

    private resourceEnabled = false;

    private gantt?: GanttStatic;

    private timelineRange?: GanttTimelineRange;

    private readonly destroy$ = new Subject<void>();

    private readonly save$ = new Subject<void>();

    private readonly rememberState$ = new Subject<void>();

    private readonly taskClick$ = new Subject<GanttTaskClick>();

    private readonly taskDblClick$ = new Subject<string | number>();

    private highlightedTaskIds = new Set<string | number>();

    private highlightedLinkIds = new Set<string | number>();

    private selectedPathRoot: string | number | null = null;

    private selectedPathRootUuid: string | null = null;

    private dependencyPathInteracted = false;

    private floatingFilters?: GanttFloatingFilters;

    private gridHeaderObserver?: MutationObserver;

    private taskSort: GanttSortState[] = [];

    private loadedTaskOrder = new Map<string, number>();

    private resourceSort: GanttSortState[] = [];

    private sortInteracted = false;

    private changes: Record<string, GanttPendingChange> = {};

    private savingChanges: Record<string, GanttPendingChange> = {};

    private columnVisibility: Record<string, boolean> = {};

    private resourceColumnVisibility: Record<string, boolean> = {};

    private viewOverrides: GanttRememberViewState = {};

    private inferredConstraintTaskIds = new Set<string | number>();

    private planningConstraints?: PlanningConstraints;

    private readonly planningChanges = new Set<string>();

    private planningSaveRequiresReload = false;

    private planningLoad?: { snapshot: PlanningConstraints; index: PlanningLoadIndex };

    private planningRowIds = new Map<string, string | number>();

    private planningDrag?: GanttPlanningDrag;

    private applyingPlanning = false;

    private planningNativeConfig?: Pick<DhtmlxGanttConfigOptions, 'work_time' | 'correct_work_time' | 'auto_scheduling_initial'>;

    private resourceBookingBaselines = new Map<string, GanttBookingBaseline>();

    private resourceGraphRefreshFrame?: number;
    private taskLabelFrame?: number;

    private saveInFlight?: Promise<GanttSaveResponse>;

    private backendOptions?: PartialGanttOptions;

    private rememberedState?: GanttRememberState;

    private resources: MachineResource[] = [];

    private lastRememberedStateJson = '';

    private lastNativeColumnSignature = '';

    private applyingRememberedState = false;

    private applyingData = false;

    private destroyed = false;

    private readVersion = 0;

    private changeVersion = 0;

    public loading = false;

    public saving = false;

    private errorMessage = '';

    private errorTranslationKey = '';

    private errorTranslationVersion = 0;

    private readonly errorTranslations = new Map<string, Promise<string>>();

    private errorToastMeasureFrame?: number;

    private chartPointerY?: number;

    public readonly errorToastTextId = 'gantt-error-' + crypto.randomUUID();

    public errorToastLabels = { showMore: 'Show more', showLess: 'Show less', close: 'Close' };

    public errorToastMessage = '';

    public errorToastExpanded = false;

    public errorToastHasDetails = false;

    public errorToastPosition: 'top' | 'bottom' = 'bottom';

    public errorToastBounds = { left: 0, top: 0, width: 0, height: 0 };

    public get saveError(): string {
        return this.errorMessage;
    }
    public set saveError(message: string) {
        this.showSaveError(message);
    }
    public showFieldNames = false;
    public get hasChanges(): boolean {
        return Object.keys(this.changes).length > 0 || this.planningChanges.size > 0;
    }
    public get toolbarColumns(): GanttColumn[] {
        return this.options.columns.filter((column) => !isGanttConfigurationColumn(getColumnPropertyKey(column)));
    }

    public get toolbarResourceColumns(): GanttColumn[] {
        return this.options.resources.columns.filter((column) => !isGanttConfigurationColumn(getColumnPropertyKey(column)));
    }

    public get criticalPathEnabled(): boolean {
        return this.criticalPath;
    }
    public get resourceGraphEnabled(): boolean {
        return this.resourceEnabled;
    }
    public get backwardPlanningEnabled(): boolean {
        return customCoerceBooleanProperty(toNativeGanttConfig(this.options).schedule_from_end ?? false);
    }

    public options: GanttOptions = createDefaultGanttOptions();

    constructor(
        private ganttService: GanttService,
        private errorHandler: ErrorHandler,
        private state: StateService,
        private utility: UtilityService,
        private modal: ModalService,
        private popup: PopupService,
        private remember: RememberService,
        private appSettings: AppSettingsService,
        private translate: TranslateService,
    ) {
        this.configureRxStreams();
    }

    async ngOnInit(): Promise<void> {
        this.translateErrorToastLabels().catch((error) => this.errorHandler.handleError(error));
        await this.read(true);
        if (this.destroyed) return;

        this.getLastRememberedState().then((state) => {
            if (this.destroyed) return;
            this.applyRememberedState(state, true);
            if (state && this.rememberedState) this.rememberCurrentStateLocal(this.rememberedState);
        });
    }

    /** Reload data; pass true to also refresh columns and server options. */
    public async read(loadSchema = false): Promise<void> {
        if (this.destroyed) return;
        if (this.saveInFlight) {
            try {
                await this.saveInFlight;
            } catch {
                /* Pending changes are protected below. */
            }
            if (this.destroyed) return;
        }
        if (this.hasChanges && !(await this.modal.openConfirmDialog({ type: 'warning', title: 'reload_data', translateTitle: true, message: 'Discard unsaved changes and reload?', ok: 'reload_data', cancel: 'cancel' }))) return;
        await this.loadGantt(loadSchema);
    }

    private async loadGantt(loadSchema = false): Promise<void> {
        if (this.destroyed) return;
        const version = ++this.readVersion;
        const changeVersion = this.changeVersion;
        this.loading = true;
        if (this.gantt) this.configureEditing(this.gantt);

        try {
            const setup = await this.getGanttConfiguration(loadSchema || !this.gantt, version);
            if (!setup || this.destroyed || version !== this.readVersion || changeVersion !== this.changeVersion) return;

            this.backendOptions = normalizeGanttOptions(setup.options);
            this.resolveOptions();

            // Validate before changing the native chart; malformed constraints never fall back silently.
            if (setup.constraints) new PlanningEngine(setup.constraints);
            this.planningConstraints = setup.constraints ?? undefined;
            this.planningChanges.clear();
            this.planningSaveRequiresReload = false;
            this.planningLoad = undefined;
            this.planningDrag = undefined;
            const tasks = this.normalizeTaskDates(setup.data);
            const resources = this.resolveResources(setup.resources, tasks);
            this.resources = resources;
            this.enableResourcePanelForAvailableResources(resources);
            if (!this.options.columns.length) this.options.columns = this.inferColumnsFromTasks(tasks);
            const assignedTasks = this.normalizeResourceBackedTaskOwners(tasks, resources);

            const existingGantt = this.gantt;
            const scroll = existingGantt?.getScrollState();
            const selectedId = existingGantt?.getSelectedId();
            this.applyRememberedState(this.rememberedState ?? this.getLastRememberedStateLocal());

            const gantt = existingGantt ?? Gantt.getGanttInstance();
            this.gantt = gantt;
            this.applyingData = true;
            try {
                if (!existingGantt) {
                    this.configurePlugins(gantt);
                    this.configureLocale(gantt);
                    this.configureZooming(gantt);
                    this.configureTemplates(gantt);
                    this.configureEvents(gantt);
                    this.floatingFilters = new GanttFloatingFilters(gantt,
                        area => (area === 'task' ? this.options.columns : this.options.resources.columns).filter(column => !this.isColumnHidden(column)),
                        () => this.syncResourceSum(gantt));
                }
                this.configureColumns(gantt);
                this.applyNativeConfig(gantt, !!existingGantt);
                this.configureResourceConfig(gantt);
                this.configureLayout(gantt);
                // Recalculate a derived backward-scheduling anchor for the refreshed tasks.
                if (this.options.config.project_end == null) gantt.config.project_end = undefined;
                this.configureAutoSchedulingProjectDates(gantt, assignedTasks);
                this.configureEditing(gantt);

                this.timelineRange?.setTasks(assignedTasks, this.toGanttDate(this.options.config.start_date), this.toGanttDate(this.options.config.end_date), this.toGanttDate(this.rememberedState?.timelineCenterDate), this.options.config.show_tasks_outside_timescale);

                if (existingGantt) {
                    gantt.clearAll();
                    gantt.resetLayout();
                } else {
                    gantt.init(this.ganttContainer.nativeElement);
                }

                // The scheduler materializes default ASAP/ALAP constraints on parse.
                // Remember which tasks supplied no constraint so direction changes
                // can update those defaults without overriding provider constraints.
                this.inferredConstraintTaskIds = new Set(assignedTasks.filter((task) => task.id !== undefined && !task['constraint_type']).map((task) => task.id!));
                this.resourceBookingBaselines.clear();
                assignedTasks.forEach((task) => this.captureResourceBookingBaseline(task));
                gantt.parse({ data: assignedTasks, links: setup.links, resources });
                this.loadedTaskOrder.clear();
                this.planningRowIds.clear();
                gantt.eachTask((task: GanttTask) => {
                    if (task.id !== undefined) this.loadedTaskOrder.set(String(task.id), this.loadedTaskOrder.size);
                    if (task._uuid != null && task.id !== undefined) this.planningRowIds.set(String(task._uuid), task.id);
                });
                this.syncResourceStore(gantt);
                this.applyTaskSort(gantt);
                this.floatingFilters?.refresh();
                this.changes = {};
                this.saveError = '';
                this.highlightedTaskIds.clear();
                this.highlightedLinkIds.clear();
                this.selectedPathRoot = null;
                this.restoreDependencyPathHighlight();
                if (selectedId && gantt.isTaskExists(selectedId)) gantt.selectTask(selectedId);
                if (scroll && this.options.config.preserve_scroll !== false) gantt.scrollTo(scroll.x, scroll.y);
                else this.restoreRememberedTimelinePosition(gantt, this.rememberedState?.timelineCenterDate);
            } finally {
                this.applyingData = false;
            }
        } finally {
            if (version === this.readVersion) {
                this.loading = false;
                if (this.gantt && !this.destroyed) this.configureEditing(this.gantt);
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const optionChange = changes['ttOptions'];
        if (!optionChange) return;
        const previous = optionChange.previousValue as PartialGanttOptions | undefined;
        const current = optionChange.currentValue as PartialGanttOptions | undefined;
        if (this.readVersion > 0 && JSON.stringify(previous?.dataTask) !== JSON.stringify(current?.dataTask)) {
            this.read(true).catch((error) => this.errorHandler.handleError(error));
            return;
        }
        if (!this.gantt) return;

        const previousColumnsSignature = this.getConfiguredColumnsSignature(this.options.columns);
        const previousResourceEnabled = this.options.resources.enabled;
        const previousResourceShowSum = this.options.resources.showSum;
        const previousResourceColumnsSignature = this.getConfiguredColumnsSignature(this.options.resources.columns);

        this.resolveOptions(true);
        this.applyRememberedState(this.rememberedState);

        const columnsChanged = previousColumnsSignature !== this.getConfiguredColumnsSignature(this.options.columns);
        const resourceLayoutChanged = previousResourceEnabled !== this.options.resources.enabled || previousResourceShowSum !== this.options.resources.showSum || previousResourceColumnsSignature !== this.getConfiguredColumnsSignature(this.options.resources.columns);

        if (columnsChanged) {
            this.configureColumns(this.gantt);
        }

        this.applyNativeConfig(this.gantt);
        this.configureResourceConfig(this.gantt);

        if (resourceLayoutChanged || columnsChanged) {
            this.configureLayout(this.gantt);
        }

        this.configureAutoSchedulingProjectDates(this.gantt);
        this.configureEditing(this.gantt);

        if (resourceLayoutChanged || columnsChanged) {
            this.gantt.resetLayout();
            this.syncResourceStore(this.gantt);
            this.floatingFilters?.refresh();
            this.gantt.refreshData();
        }

        this.gantt.render();
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.timelineRange?.destroy();
        this.floatingFilters?.destroy();
        this.gridHeaderObserver?.disconnect();
        if (this.taskLabelFrame !== undefined) cancelAnimationFrame(this.taskLabelFrame);
        if (this.errorToastMeasureFrame !== undefined) cancelAnimationFrame(this.errorToastMeasureFrame);
        if (this.resourceGraphRefreshFrame !== undefined) cancelAnimationFrame(this.resourceGraphRefreshFrame);
        this.resourceBookingBaselines.clear();
        this.planningLoad = undefined;
        this.planningDrag = undefined;
        this.readVersion++;
        this.loading = false;
        this.destroy$.next();
        this.destroy$.complete();

        if (this.gantt) {
            this.rememberCurrentState();
            this.gantt.destructor();
        }
    }

    private configureRxStreams(): void {
        this.save$.pipe(debounceTime(150), takeUntil(this.destroy$)).subscribe(() => {
            if (this.options.save.autoSave && this.hasChanges && !this.planningDrag && !this.planningSaveRequiresReload) this.save().catch((error) => this.errorHandler.handleError(error));
        });

        this.rememberState$.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe(() => this.rememberCurrentState());

        this.taskClick$
            .pipe(
                switchMap((id) =>
                    timer(250).pipe(
                        takeUntil(this.taskDblClick$),
                        map(() => id),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(({ id, column, newTab, area }) => {
                // Task bars reserve single-click for the dependency path; navigation uses double-click.
                if (area === 'timeline') {
                    this.highlightDependencyPath(id);
                } else if (column?.clickType) {
                    this.handleCellEvent(id, column, 'click', newTab, area).catch((error) => this.errorHandler.handleError(error));
                } else {
                    if (area !== 'resource') this.highlightDependencyPath(id);
                }
            });
    }

    private configurePlugins(gantt: GanttStatic): void {
        gantt.plugins({ multiselect: true });
        gantt.plugins({ critical_path: true });
        gantt.plugins({ auto_scheduling: true });
        gantt.plugins({ resources: true } as any);

        gantt.config.multiselect = this.multiSelect;
        gantt.config.highlight_critical_path = this.criticalPath;
    }

    private configureLocale(gantt: GanttStatic): void {
        gantt.i18n.setLocale('no');

        gantt.config.date_format = '%Y-%m-%d %H:%i:%s';
        gantt.config.auto_types = true;
        gantt.config.duration_unit = 'hour';
        gantt.config.time_step = 15;

        gantt.config['scale_unit'] = 'day';
        gantt.config['date_scale'] = '%d %M';
        gantt.config['subscales'] = [{ unit: 'hour', step: 4, date: '%H:%i' }];
    }

    private configureZooming(gantt: GanttStatic): void {
        this.timelineRange = new GanttTimelineRange(gantt);
        configureGanttZoom(gantt, this.options.zoom, this.rememberedState?.zoomLevel, this.timelineRange);

        gantt.ext.zoom.attachEvent('onAfterZoom', () => {
            if (!this.applyingRememberedState) {
                this.rememberState$.next();
            }
        });
    }

    private configureEditing(gantt: GanttStatic): void {
        const readonly = this.loading || customCoerceBooleanProperty(this.options.config.readonly ?? false);
        const tasksEditable = this.options.editing.tasks && !readonly;
        const drag = this.options.editing.drag;
        const dragLinks = !readonly && this.linksEditable && this.resolveDragOption(drag.links, this.linksEditable);
        const dragMove = tasksEditable && this.resolveDragOption(drag.move, tasksEditable);
        const dragResize = tasksEditable && this.resolveDragOption(drag.resize, tasksEditable);
        const dragProgress = tasksEditable && this.resolveDragOption(drag.progress, tasksEditable);

        gantt.config.readonly = readonly;

        gantt.config.drag_links = dragLinks && !this.planningConstraints;
        gantt.config.drag_move = dragMove;
        gantt.config.drag_resize = dragResize && !this.planningConstraints;
        gantt.config.drag_progress = dragProgress;

        if (drag.multiple !== null && drag.multiple !== undefined) {
            gantt.config.drag_multiple = this.resolveDragOption(drag.multiple, false);
        }

        if (drag.project !== null && drag.project !== undefined) {
            gantt.config.drag_project = this.resolveDragOption(drag.project, false);
        }

        if (drag.lightbox !== null && drag.lightbox !== undefined) {
            gantt.config.drag_lightbox = this.resolveDragOption(drag.lightbox, false);
        }

        if (this.planningConstraints) {
            gantt.config.drag_multiple = false;
            gantt.config.drag_project = false;
        }
        this.configureResourceConfig(gantt);
    }

    private resolveDragOption(value: unknown, fallback: boolean): boolean {
        return value === null || value === undefined ? fallback : customCoerceBooleanProperty(value as any);
    }

    private configureResourceConfig(gantt: GanttStatic): void {
        // Separate footer data never enters resource assignments or the scrolling list.
        if (!gantt.getDatastore('resourceSum')) {
            gantt.createDatastore({ name: 'resourceSum', type: 'treeDatastore' });
        }
        gantt.config.resources = this.getGanttResourcesConfig();
        gantt.config.resource_store = 'resource';
        gantt.config.resource_property = 'owner';
        gantt.config.resource_render_empty_cells = true;
        (gantt as any).resource_table = {
            ...(gantt as any).resource_table,
            columns: this.getNativeResourceColumns(),
            row_height: gantt.config.row_height,
        };
    }

    private configureColumns(gantt: GanttStatic): void {
        gantt.config.columns = this.options.columns.filter((column) => !this.isColumnHidden(column)).map((column) => this.toGanttGridColumn(column));
    }

    private configureLayout(gantt: GanttStatic): void {
        const nativeResourceColumns = this.getNativeResourceColumns();
        const resourcesEnabled = this.resolveResourcesEnabled();
        const resourceRows = resourcesEnabled
            ? [
                  { resizer: true, width: 1 },
                  {
                      gravity: 1,
                      config: {
                          columns: nativeResourceColumns,
                          resource_store: 'resource',
                          resource_property: 'owner',
                          row_height: gantt.config.row_height,
                      },
                      cols: [
                          {
                              view: 'resourceGrid',
                              id: 'resourceGrid',
                              bind: 'resource',
                              group: 'grids',
                              width: 260,
                              config: {
                                  columns: nativeResourceColumns,
                                  resource_store: 'resource',
                                  resource_property: 'owner',
                                  row_height: gantt.config.row_height,
                              },
                              scrollY: 'resourceVScroll',
                          },
                          { resizer: true, width: 1 },
                          {
                              view: 'resourceHistogram',
                              id: 'resourceHistogram',
                              bind: 'resource',
                              capacity: 24,
                              scrollX: 'scrollHor',
                              scrollY: 'resourceVScroll',
                          },
                          { view: 'scrollbar', id: 'resourceVScroll', group: 'vertical' },
                      ],
                  },
              ]
            : [];

        const sumConfig = {
            columns: nativeResourceColumns,
            resource_store: 'resourceSum',
            resource_property: 'owner',
            row_height: gantt.config.row_height,
            scale_height: 0,
        };
        const resourceSumRows =
            resourcesEnabled && this.options.resources.showSum && this.resources.length
                ? [
                      {
                          id: 'resourceSumFooter',
                          css: 'tt-gantt-resource-sum',
                          height: gantt.config.row_height + 2,
                          config: sumConfig,
                          cols: [
                              { view: 'resourceGrid', id: 'resourceSumGrid', bind: 'resourceSum', group: 'grids', width: 260, config: sumConfig },
                              { resizer: true, width: 1 },
                              { view: 'resourceHistogram', id: 'resourceSumHistogram', bind: 'resourceSum', capacity: 24, config: sumConfig, scrollX: 'scrollHor' },
                              { view: 'scrollbar', id: 'resourceSumVScroll', group: 'vertical' },
                          ],
                      },
                  ]
                : [];

        gantt.config.layout = {
            css: 'gantt_container',
            rows: [
                {
                    gravity: 2,
                    cols: [
                        { view: 'grid', group: 'grids', scrollY: 'scrollVer' },
                        { resizer: true, width: 1 },
                        { view: 'timeline', scrollX: 'scrollHor', scrollY: 'scrollVer' },
                        { view: 'scrollbar', id: 'scrollVer', group: 'vertical' },
                    ],
                },
                ...resourceRows,
                ...resourceSumRows,
                { view: 'scrollbar', id: 'scrollHor' },
            ],
        };
    }

    private configureTemplates(gantt: GanttStatic): void {
        // Descriptions are provider-owned plain text. Native hover keeps short bars readable.
        gantt.templates.task_text = (_start, _end, task) => {
            const text = escapeGanttColumnText(String(task.text ?? ''));
            return '<span class="tt-gantt-task-label" title="' + text + '">' + text + '</span>';
        };

        gantt.templates.timeline_cell_class = (_task, date) => {
            return gantt.isWorkTime(date) ? '' : 'week_end';
        };

        gantt.templates.task_class = (_start, _end, task) => {
            if (this.selectedPathRoot != null && task.id === this.selectedPathRoot) {
                return 'dep-root-task';
            }

            if (this.highlightedTaskIds.has(task.id)) {
                return 'dep-path-task';
            }

            return '';
        };

        gantt.templates.link_class = (link) => {
            return this.highlightedLinkIds.has(link.id) ? 'dep-path-link' : '';
        };

        this.configureResourceHistogramTemplates(gantt);
    }

    private configureResourceHistogramTemplates(gantt: GanttStatic): void {
        const getBookedHours = (task: GanttTask, cellStart: Date, cellEnd: Date): number => {
            // Preview the booking profile against the current bar. Source slots
            // remain unchanged; moves shift them and resizing scales their spacing.
            // The booked effort stays constant and gaps never acquire hours.
            if (Array.isArray(task.planned_segments)) {
                const baseline = task._uuid == null ? undefined : this.resourceBookingBaselines.get(String(task._uuid));
                const currentStart = this.toGanttDate(task.start_date)?.getTime();
                const currentEnd = this.toGanttDate(task.end_date)?.getTime();
                if (baseline && (currentStart === undefined || currentEnd === undefined || currentEnd <= currentStart)) return 0;
                const scale = baseline ? (currentEnd! - currentStart!) / (baseline.end - baseline.start) : 1;
                return task.planned_segments.reduce((sum, segment) => {
                    if (!segment || typeof segment !== 'object') return sum;
                    const start = this.toGanttDate(segment.start_date);
                    const end = this.toGanttDate(segment.end_date);
                    const hours = Number(segment.hours_planned);
                    if (!start || !end || end <= start || !Number.isFinite(hours) || hours <= 0) return sum;
                    const previewStart = baseline ? currentStart! + (start.getTime() - baseline.start) * scale : start.getTime();
                    const previewEnd = baseline ? currentStart! + (end.getTime() - baseline.start) * scale : end.getTime();
                    const overlap = Math.max(0, Math.min(previewEnd, cellEnd.getTime()) - Math.max(previewStart, cellStart.getTime()));
                    return sum + (hours * overlap) / (previewEnd - previewStart);
                }, 0);
            }

            // Generic providers without explicit slots retain their date-span load.
            const start = this.toGanttDate(task.start_date);
            const end = this.toGanttDate(task.end_date);
            if (!start || !end) return 0;
            return Math.max(0, Math.min(end.getTime(), cellEnd.getTime()) - Math.max(start.getTime(), cellStart.getTime())) / (1000 * 60 * 60);
        };

        const getTotalBooked = (start: Date, end: Date, resource: MachineResource, tasks: GanttTask[]) => {
            const snapshot = this.planningDrag?.result?.snapshot ?? this.planningConstraints;
            if (snapshot) {
                const from = planningTime(planningDateFromDate(start, snapshot.time_basis), snapshot.time_basis);
                const to = planningTime(planningDateFromDate(end, snapshot.time_basis), snapshot.time_basis);
                const index = this.getPlanningLoad(snapshot);
                const resourceIds = resource.isGanttResourceSum ? this.getGraphResources().map((item) => String(item.id)) : [String(resource.id)];
                return resourceIds.reduce((sum, id) => sum + index.booked(id, from, to), 0);
            }
            const allocated = resource.isGanttResourceSum ? this.getGraphResources().flatMap((item) => this.getResourceGraphTasks(item, [])) : this.getResourceGraphTasks(resource, tasks);
            return allocated.reduce((sum, task) => sum + getBookedHours(task, start, end), 0);
        };

        const getTotalCapacity = (start: Date, end: Date, resource: MachineResource): number | undefined => {
            const snapshot = this.planningConstraints;
            if (!snapshot) return this.getResourceCapacity(resource);
            const from = planningTime(planningDateFromDate(start, snapshot.time_basis), snapshot.time_basis);
            const to = planningTime(planningDateFromDate(end, snapshot.time_basis), snapshot.time_basis);
            const index = this.getPlanningLoad(this.planningDrag?.result?.snapshot ?? snapshot);
            const resourceIds = resource.isGanttResourceSum ? this.getGraphResources().map((item) => String(item.id)) : [String(resource.id)];
            return resourceIds.reduce((sum, id) => sum + index.available(id, from, to), 0);
        };

        gantt.templates.histogram_cell_allocated = (start, end, resource, tasks) => {
            return getTotalBooked(start, end, resource, tasks);
        };

        gantt.templates.histogram_cell_label = (start, end, resource, tasks) => {
            const booked = getTotalBooked(start, end, resource, tasks);
            return booked ? `${Math.round(booked * 100) / 100}h` : '';
        };

        gantt.templates.histogram_cell_capacity = (start, end, resource) => {
            return getTotalCapacity(start, end, resource) ?? -1;
        };

        gantt.templates.histogram_cell_class = (start, end, resource, tasks) => {
            const booked = getTotalBooked(start, end, resource, tasks);
            const capacity = getTotalCapacity(start, end, resource);

            const totalClass = resource.isGanttResourceSum ? 'hist-sum ' : '';
            if (capacity === undefined) return totalClass.trim();
            if (booked > capacity) return totalClass + 'hist-overload';
            if (booked > 0 && booked === capacity) return totalClass + 'hist-full';
            return totalClass.trim();
        };
    }

    private captureResourceBookingBaseline(task: GanttTask): void {
        if (task._uuid == null) return;
        const segments = Array.isArray(task.planned_segments) ? task.planned_segments : [];
        const starts: number[] = [];
        const ends: number[] = [];
        for (const segment of segments) {
            if (!segment || typeof segment !== 'object') continue;
            const start = this.toGanttDate(segment.start_date)?.getTime();
            const end = this.toGanttDate(segment.end_date)?.getTime();
            const hours = Number(segment.hours_planned);
            if (start === undefined || end === undefined || end <= start || !Number.isFinite(hours) || hours <= 0) continue;
            starts.push(start);
            ends.push(end);
        }
        const start = this.toGanttDate(task.start_date)?.getTime() ?? (starts.length ? Math.min(...starts) : undefined);
        const end = this.toGanttDate(task.end_date)?.getTime() ?? (ends.length ? Math.max(...ends) : undefined);
        if (start !== undefined && end !== undefined && end > start) {
            this.resourceBookingBaselines.set(String(task._uuid), { start, end });
        }
    }

    private queueResourceGraphRefresh(): void {
        if (!this.gantt || !this.resourceEnabled || this.destroyed || this.resourceGraphRefreshFrame !== undefined) return;
        this.resourceGraphRefreshFrame = requestAnimationFrame(() => {
            this.resourceGraphRefreshFrame = undefined;
            if (!this.gantt || this.destroyed) return;
            this.gantt.getDatastore(this.gantt.config.resource_store)?.refresh();
            this.gantt.getDatastore('resourceSum')?.refresh();
        });
    }

    private configureEvents(gantt: GanttStatic): void {
        gantt.attachEvent('onTaskClick', (id, event) => this.onTaskClick(id, event));
        gantt.attachEvent('onTaskDblClick', (id, event) => this.onTaskDblClick(id, event));
        gantt.attachEvent('onEmptyClick', (event) => this.onEmptyClick(event));
        // Native header sorting targets the task store only. Resource headers use their own store.
        gantt.attachEvent('onGridHeaderClick', (name, event) => {
            // Native delegation reports the clicked child, which may not carry the column ID.
            const header = this.getClickTarget(event)?.closest('.gantt_grid_head_cell');
            const columnName = header?.getAttribute('data-column-name') ?? name;
            if (columnName === 'add') return true;
            this.sortGridColumn(columnName, 'task', (event as MouseEvent)?.shiftKey === true);
            return false;
        });

        gantt.attachEvent('onBeforeLightbox', () => false);

        gantt.attachEvent('onBeforeTaskAdd', (_id, task) => {
            if (!this.applyingData) {
                task['_uuid'] = crypto.randomUUID();
                this.captureResourceBookingBaseline(task);
            }
            return true;
        });
        gantt.attachEvent('onBeforeLinkAdd', (_id, link) => {
            if (!this.linksEditable || this.planningConstraints) return false;
            if (!this.applyingData) link['_uuid'] = crypto.randomUUID();
            return true;
        });
        gantt.attachEvent('onBeforeLinkUpdate', () => this.linksEditable && !this.planningConstraints);
        gantt.attachEvent('onBeforeLinkDelete', () => this.linksEditable && !this.planningConstraints);

        for (const [nativeEvent, event] of [
            ['onAfterLinkAdd', 'add'],
            ['onAfterLinkUpdate', 'update'],
            ['onAfterLinkDelete', 'delete'],
        ] as const) {
            gantt.attachEvent(nativeEvent, (id, link) => this.trackChange({ event, entity: 'link', id, parentId: null, link, task: null, dragMode: null, predecessor: null }));
        }

        gantt.attachEvent('onBeforeAutoSchedule', () => !this.planningConstraints);
        gantt.attachEvent('onBeforeTaskDrag', (id, mode, event) => {
            if (event instanceof MouseEvent) this.onChartPointerMove(event);
            if (this.planningConstraints && (this.loading || this.saving || this.planningSaveRequiresReload)) return false;
            if (!this.planningConstraints || mode === gantt.config.drag_mode.progress) return true;
            const task = gantt.getTask(id);
            const assignment = this.planningConstraints.assignments.find((item) => item.row_uuid === String(task['_uuid']));
            if (!assignment || mode !== gantt.config.drag_mode.move || this.loading || this.saving) return false;
            this.planningDrag = { source: this.planningConstraints, engine: new PlanningEngine(this.planningConstraints), taskId: assignment.task_id, rowUuids: new Set(this.planningConstraints.assignments.filter((item) => item.task_id === assignment.task_id).map((item) => item.row_uuid)) };
            this.saveError = '';
            return true;
        });
        gantt.attachEvent('onBeforeTaskChanged', (id, mode) => {
            if (!this.planningConstraints || mode === gantt.config.drag_mode.progress) return true;
            if (!this.planningDrag?.request) this.previewPlanningDrag(id, mode);
            const drag = this.planningDrag;
            if (!drag?.result || drag.error) {
                if (drag) this.applyPlanningSnapshot(drag.source, drag.rowUuids);
                this.planningDrag = undefined;
                if (this.options.save.autoSave && this.hasChanges && !this.planningSaveRequiresReload) this.save$.next();
                return false;
            }
            this.planningConstraints = drag.result.snapshot;
            this.applyPlanningSnapshot(drag.result.snapshot, drag.rowUuids);
            return true;
        });

        gantt.attachEvent('onAfterAutoSchedule', () => {
            gantt.refreshData();
            this.queueResourceGraphRefresh();
        });
        gantt.attachEvent('onTaskDrag', (id, mode, _task, event) => {
            if (event instanceof MouseEvent) this.onChartPointerMove(event, true);
            if (mode !== gantt.config.drag_mode.progress) {
                if (this.planningConstraints) this.previewPlanningDrag(id, mode);
                this.queueResourceGraphRefresh();
            }
        });
        gantt.attachEvent('onAfterTaskDrag', (id, mode) => {
            const planned = this.planningDrag?.result;
            if (planned && this.planningConstraints && mode !== gantt.config.drag_mode.progress) {
                if (this.planningConstraints.save_mode === 'validated-plan') for (const assignment of planned.assignments) this.planningChanges.add(assignment.id);
                for (const assignment of planned.assignments) {
                    const task = this.findPlanningRow(assignment.row_uuid);
                    if (!task || task.id === undefined) continue;
                    this.trackChange({ event: task.id === id ? 'drag' : 'autoschedule', entity: 'task', id: task.id, parentId: task.parent ?? null, task, dragMode: task.id === id ? 'move' : null, link: null, predecessor: null });
                }
                this.planningDrag = undefined;
                this.queueResourceGraphRefresh();
                return;
            }
            this.trackChange({ event: 'drag', entity: 'task', id, parentId: gantt.getTask(id).parent ?? null, task: gantt.getTask(id), dragMode: mode === 'move' || mode === 'resize' || mode === 'progress' || mode === 'ignore' ? mode : null, link: null, predecessor: null });
            this.queueResourceGraphRefresh();
        });

        gantt.attachEvent('onAfterTaskAutoSchedule', (task, _start, link, predecessor) => {
            this.trackChange({
                event: 'autoschedule',
                dragMode: null,
                entity: 'task',
                id: task.id,
                parentId: task.parent ?? null,
                task,
                link,
                predecessor,
            });
        });

        gantt.attachEvent('onAfterTaskMove', (id, parent) => {
            this.trackChange({
                event: 'move',
                dragMode: null,
                entity: 'task',
                id,
                parentId: parent ?? null,
                task: null,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onAfterTaskUpdate', (id, task) => {
            this.trackChange({
                event: 'update',
                dragMode: null,
                entity: 'task',
                id,
                parentId: task.parent ?? null,
                task,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onAfterTaskAdd', (id, task) => {
            this.trackChange({
                event: 'add',
                dragMode: null,
                entity: 'task',
                id,
                parentId: task.parent ?? null,
                task,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onAfterTaskDelete', (id, task) => {
            this.trackChange({
                event: 'delete',
                dragMode: null,
                entity: 'task',
                id,
                parentId: task.parent ?? null,
                task,
                link: null,
                predecessor: null,
            });
        });

        gantt.attachEvent('onGridResizeEnd', (_oldWidth, newWidth) => {
            if (!this.applyingRememberedState) {
                this.options.config.grid_width = newWidth;
                gantt.config.grid_width = newWidth;
                this.rememberState$.next();
            }

            return true;
        });

        gantt.attachEvent('onColumnResizeEnd', () => {
            if (!this.applyingRememberedState) {
                this.rememberState$.next();
            }

            return true;
        });

        gantt.attachEvent('onGanttRender', () => {
            this.queueTaskLabelPosition(gantt);
            this.refreshTimelineViewport(gantt);
            this.updateSortIndicators();
            this.floatingFilters?.render();
            this.observeGridHeaders();
            this.rememberColumnStateIfChanged();
            this.updateErrorToastPosition();
        });

        gantt.attachEvent('onGanttScroll', () => {
            this.queueTaskLabelPosition(gantt);
            if (!this.applyingRememberedState) {
                this.rememberState$.next();
            }
        });

        gantt.attachEvent('onDataRender', () => this.queueTaskLabelPosition(gantt));
        gantt.attachEvent('onTaskDrag', () => this.queueTaskLabelPosition(gantt));

        gantt.attachEvent('onGanttReady', () => {
            this.ttReady.emit(gantt);
        });
    }

    private queueTaskLabelPosition(gantt: GanttStatic): void {
        if (this.destroyed || this.taskLabelFrame !== undefined) return;
        this.taskLabelFrame = requestAnimationFrame(() => {
            this.taskLabelFrame = undefined;
            if (this.destroyed || !gantt.$task || !gantt.$task_data) return;
            const viewport = gantt.$task.getBoundingClientRect();
            const left = viewport.left + gantt.$task.clientLeft;
            const right = left + gantt.$task.clientWidth;
            // Read only rendered bars, then write styles together to avoid layout
            // thrashing. Moving labels must never repaint the complete timeline.
            const positions = Array.from(gantt.$task_data.querySelectorAll<HTMLElement>('.tt-gantt-task-label')).map(label => {
                const content = label.parentElement!;
                const bar = content.closest<HTMLElement>('.gantt_task_line');
                if (!bar || bar.classList.contains('gantt_milestone')) return;
                const bounds = bar.getBoundingClientRect();
                const width = Math.max(0, Math.min(bounds.right, right) - Math.max(bounds.left, left));
                const offset = Math.max(0, left - bounds.left);
                const long = bounds.width > gantt.$task.clientWidth && width > 0;
                return { content, left: long ? offset + 'px' : '', width: long ? width + 'px' : '',
                    align: long && label.getBoundingClientRect().width > width ? 'left' : '' };
            });
            for (const position of positions) {
                if (!position) continue;
                if (position.content.style.left !== position.left) position.content.style.left = position.left;
                if (position.content.style.width !== position.width) position.content.style.width = position.width;
                if (position.content.style.textAlign !== position.align) position.content.style.textAlign = position.align;
            }
        });
    }

    public onChartPointerMove(event: MouseEvent, dragging = false): void {
        if (event.target instanceof Element && event.target.closest('.gantt-error-toast')) return;
        this.chartPointerY = event.clientY;
        if (dragging && this.saveError) this.updateErrorToastPosition(true);
    }

    public dismissErrorToast(): void {
        this.saveError = '';
    }

    public toggleErrorToastDetails(): void {
        this.errorToastExpanded = !this.errorToastExpanded;
        this.queueErrorToastMeasure();
    }

    private showSaveError(message: string, key = message, params: Record<string, string> = {}): void {
        if (message === this.errorMessage && key === this.errorTranslationKey) return;
        const opened = !!this.errorMessage;
        this.errorMessage = message;
        this.errorTranslationKey = key;
        this.errorToastMessage = message;
        this.errorToastExpanded = false;
        this.errorToastHasDetails = false;
        const version = ++this.errorTranslationVersion;
        if (!message) return;
        this.updateErrorToastPosition(!opened);
        this.queueErrorToastMeasure();
        let translation = this.errorTranslations.get(key);
        if (!translation) {
            translation = this.translate
                .translate(key)
                .then((value) => (typeof value === 'string' ? value : key))
                .catch(() => key);
            this.errorTranslations.set(key, translation);
        }
        translation.then((value) => {
            if (this.destroyed || version !== this.errorTranslationVersion) return;
            this.errorToastMessage = value.trim() && value !== key ? value.replace(/\{(\w+)\}/g, (token, name) => params[name] ?? token) : message;
            this.queueErrorToastMeasure();
        });
    }

    private async translateErrorToastLabels(): Promise<void> {
        await Promise.all(
            (Object.keys(this.errorToastLabels) as Array<keyof typeof this.errorToastLabels>).map(async (label) => {
                const key = { showMore: 'show_more', showLess: 'show_less', close: 'close' }[label];
                const value = await this.translate.translate(key);
                if (!this.destroyed && typeof value === 'string' && value.trim() && value !== key) this.errorToastLabels[label] = value;
            }),
        );
    }

    private queueErrorToastMeasure(): void {
        if (this.destroyed || !this.saveError || this.errorToastMeasureFrame !== undefined) return;
        this.errorToastMeasureFrame = requestAnimationFrame(() => {
            this.errorToastMeasureFrame = undefined;
            const text = this.errorToastText?.nativeElement;
            if (!this.destroyed && this.saveError && text?.isConnected && !this.errorToastExpanded) {
                this.errorToastHasDetails = text.scrollHeight > text.clientHeight + 1;
            }
        });
    }

    private updateErrorToastPosition(moveToast = false): void {
        if (!this.saveError || this.destroyed) return;
        const viewport = this.ganttViewport?.nativeElement.getBoundingClientRect();
        if (!viewport) return;
        // Use the visible task canvas, excluding the sidebar, scale and resource
        // graph. Intersect its scrollable content with the viewport when zoomed.
        const pane = this.gantt?.$task?.getBoundingClientRect() ?? viewport;
        const data = this.gantt?.$task_data?.getBoundingClientRect() ?? pane;
        const left = Math.max(viewport.left, pane.left, data.left);
        const top = Math.max(viewport.top, pane.top, data.top);
        const right = Math.min(viewport.right, pane.right, data.right);
        const bottom = Math.min(viewport.bottom, pane.bottom, data.bottom);
        this.errorToastBounds = { left: left - viewport.left, top: top - viewport.top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
        if (moveToast) this.errorToastPosition = this.chartPointerY !== undefined && this.chartPointerY > (top + bottom) / 2 ? 'top' : 'bottom';
        this.queueErrorToastMeasure();
    }

    public onTaskClick(id: string | number, event?: Event): boolean {
        if (this.isResourceGridEvent(event) || this.isTreeControlClick(event)) return true;
        this.taskClick$.next({ id, column: this.getClickedColumn(event), newTab: this.isModifierClick(event), area: this.getTaskClickArea(event) });
        return true;
    }

    public onTaskDblClick(id: string | number, event?: Event): boolean {
        if (!this.gantt) return false;
        if (this.isResourceGridEvent(event)) return true;
        this.taskDblClick$.next(id);
        if (this.isTreeControlClick(event)) return true;

        const column = this.getClickedColumn(event);
        const action = column?.doubleClickType ?? column?.clickType;
        if (column && action) {
            // The double click cancels the delayed single click; execute its action once.
            this.handleCellEvent(id, column, 'dblclick', this.isModifierClick(event), this.getTaskClickArea(event)).catch((error) => this.errorHandler.handleError(error));
        }
        return true;
    }

    private getClickedColumn(event?: Event): GanttColumn | undefined {
        const target = this.getClickTarget(event);
        // Timeline bars share the text column's actions, including when it is hidden in the grid.
        if (this.getTaskClickArea(event) === 'timeline') return this.options.columns.find((column) => getColumnPropertyKey(column) === 'text');
        const cell = target?.closest('.gantt_cell[data-column-name]');
        const grid = this.gantt?.$grid;
        // Native selection may rerender the row before onTaskClick reaches us.
        if (!cell || !grid || (!grid.contains(cell) && !event?.composedPath().includes(grid))) return undefined;
        const propertyKey = cell.getAttribute('data-column-name');
        return this.options.columns.find((column) => !this.isColumnHidden(column) && getColumnPropertyKey(column) === propertyKey);
    }

    private getTaskClickArea(event?: Event): GanttNavigationArea {
        const bar = this.getClickTarget(event)?.closest('.gantt_task_line');
        const timeline = this.gantt?.$task;
        return bar && timeline && (timeline.contains(bar) || event?.composedPath().includes(timeline)) ? 'timeline' : 'grid';
    }

    private isResourceGridEvent(event?: Event): boolean {
        const grid: HTMLElement | undefined = this.gantt?.getLayoutView('resourceGrid')?.$grid;
        const target = this.getClickTarget(event);
        return !!grid && !!target && (grid.contains(target) || !!event?.composedPath().includes(grid));
    }

    public onResourceCellClick(event: MouseEvent, trigger: GanttActionTrigger): void {
        if (!this.gantt || !this.isResourceGridEvent(event) || this.isTreeControlClick(event)) return;
        const view = this.gantt.getLayoutView('resourceGrid');
        const header = this.getClickTarget(event)?.closest('.gantt_grid_head_cell');
        if (header) {
            if (trigger === 'click') this.sortGridColumn(header.getAttribute('data-column-name') ?? '', 'resource', event.shiftKey);
            return;
        }
        const cell = this.getClickTarget(event)?.closest('.gantt_cell[data-column-name]');
        const rowElement = cell?.closest('.gantt_row');
        // Use the resource view's native row attribute, never a task lookup with the same ID.
        const id = rowElement?.getAttribute(view.$config.item_attribute);
        const column = this.getVisibleResourceColumns().find((item) => getColumnPropertyKey(item) === cell?.getAttribute('data-column-name'));
        if (id == null || !column) return;
        if (trigger === 'click') {
            this.taskClick$.next({ id, column, newTab: this.isModifierClick(event), area: 'resource' });
        } else {
            this.taskDblClick$.next(id);
            if (column.doubleClickType ?? column.clickType) this.handleCellEvent(id, column, trigger, this.isModifierClick(event), 'resource').catch((error) => this.errorHandler.handleError(error));
        }
    }

    private getClickTarget(event?: Event): Element | null {
        return event?.target instanceof Element ? event.target : event?.target instanceof Node ? event.target.parentElement : null;
    }

    private isTreeControlClick(event?: Event): boolean {
        return !!this.getClickTarget(event)?.closest('.gantt_tree_icon.gantt_open, .gantt_tree_icon.gantt_close, .gantt_add, .gantt_task_drag, .gantt_link_control');
    }

    private isModifierClick(event?: Event): boolean {
        return event instanceof MouseEvent || event instanceof KeyboardEvent ? this.utility.isModifierKeyPressed(event) : false;
    }

    private async handleCellEvent(id: string | number, column: GanttColumn, trigger: GanttActionTrigger, newTab: boolean, area: GanttNavigationArea): Promise<void> {
        if (this.destroyed || !this.gantt) return;
        const resourceStore = area === 'resource' ? this.gantt.getDatastore(this.gantt.config.resource_store) : undefined;
        const row: GanttTask | MachineResource | undefined = resourceStore?.exists(id) ? (resourceStore.getItem(id) as MachineResource) : area !== 'resource' && this.gantt.isTaskExists(id) ? this.gantt.getTask(id) : undefined;
        if (!row || row['isGanttResourceSum']) return;
        const propertyKey = getColumnPropertyKey(column);
        const value = row[propertyKey];
        if (value == null || value === '') return;

        const schemaState = column.state?.trim();
        const response = schemaState
            ? {
                  type: trigger === 'dblclick' ? (column.doubleClickType ?? column.clickType ?? 'default') : (column.clickType ?? 'default'),
                  state: schemaState,
                  params: column.stateParams ?? '',
              }
            : await this.ganttService.getGotoParms({
                  loadDataTask: this.options.dataTask?.loadData,
                  field: propertyKey,
                  trigger,
                  area,
                  row,
                  rememberId: this.ttRememberId,
              });
        if (this.destroyed || !response.state) return;

        if (response.type === 'default') {
            if (newTab) this.state.newTab(response.state, response.params);
            else await this.state.go(response.state, response.params);
        } else if (response.type === 'modal') {
            await this.modal.openStateDialog({ state: response.state, params: response.params });
            await this.readAfterCellAction(column);
        } else if (response.type === 'popup') {
            const popup = await this.popup.openPopup(response.state + (response.params ? '/' + response.params : ''), {
                rememberId: this.ttRememberId ? this.ttRememberId + '.cellclick:' + propertyKey : undefined,
            });
            if (this.destroyed) return;
            popup?.closed.pipe(takeUntil(this.destroy$)).subscribe((closed) => {
                if (closed) this.readAfterCellAction(column).catch((error) => this.errorHandler.handleError(error));
            });
        }
    }

    private async readAfterCellAction(column: GanttColumn): Promise<void> {
        if (this.destroyed) return;
        if (customCoerceBooleanProperty(column.rebindOnClose ?? false)) await this.read(true);
        else if (customCoerceBooleanProperty(column.readOnClose ?? true)) await this.read();
    }

    public onEmptyClick(event?: Event): boolean {
        if (this.getClickTarget(event)?.closest('.gantt_grid_head_cell')) return true;
        this.clearDependencyPathHighlight();
        return true;
    }

    private highlightDependencyPath(taskId: string | number, remember = true): void {
        if (!this.gantt || !this.gantt.isTaskExists(taskId)) return;

        const gantt = this.gantt;
        const task = gantt.getTask(taskId) as GanttTask;
        this.selectedPathRootUuid = task._uuid == null ? null : String(task._uuid);
        const links = gantt.getLinks();

        this.selectedPathRoot = taskId;
        this.highlightedTaskIds.clear();
        this.highlightedLinkIds.clear();

        const forwardVisited = new Set<string | number>();
        const backwardVisited = new Set<string | number>();

        const walkForward = (currentId: string | number) => {
            if (forwardVisited.has(currentId)) return;
            forwardVisited.add(currentId);

            for (const link of links) {
                if (link.source === currentId) {
                    this.highlightedLinkIds.add(link.id);
                    this.highlightedTaskIds.add(link.target);
                    walkForward(link.target);
                }
            }
        };

        const walkBackward = (currentId: string | number) => {
            if (backwardVisited.has(currentId)) return;
            backwardVisited.add(currentId);

            for (const link of links) {
                if (link.target === currentId) {
                    this.highlightedLinkIds.add(link.id);
                    this.highlightedTaskIds.add(link.source);
                    walkBackward(link.source);
                }
            }
        };

        this.highlightedTaskIds.add(taskId);

        walkForward(taskId);
        walkBackward(taskId);

        gantt.render();
        if (remember) {
            this.dependencyPathInteracted = true;
            this.rememberViewState();
        }
    }

    private restoreDependencyPathHighlight(): void {
        if (!this.gantt) return;
        this.selectedPathRoot = null;
        this.highlightedTaskIds.clear();
        this.highlightedLinkIds.clear();
        // Native IDs and links may change on a reread. Keep only the stable root identity.
        let rootId: string | number | undefined;
        if (this.selectedPathRootUuid !== null) {
            this.gantt.eachTask((task: GanttTask) => {
                if (task._uuid != null && String(task._uuid) === this.selectedPathRootUuid) rootId = task.id;
            });
        }
        if (rootId !== undefined) this.highlightDependencyPath(rootId, false);
        else this.gantt.render();
    }

    public clearDependencyPathHighlight(): void {
        if (!this.gantt) return;

        this.selectedPathRoot = null;
        this.selectedPathRootUuid = null;
        this.highlightedTaskIds.clear();
        this.highlightedLinkIds.clear();
        this.dependencyPathInteracted = true;

        this.gantt.render();
        this.rememberViewState();
    }

    private trackChange(change: GanttTaskChangeInfo): void {
        if (this.applyingData || this.applyingPlanning || this.destroyed) return;
        const row = this.getChangeRow(change);
        if (change.entity === 'task' && row?.['render'] === 'split') return;
        if (row?._uuid === undefined || row._uuid === null || String(row._uuid).trim() === '') {
            this.showSaveError('The changed Gantt row has no _uuid. Add a unique row key in its provider procedure.', 'gantt_row_uuid_missing');
            return;
        }
        const key = change.entity + ':' + String(row._uuid);
        const previous = this.changes[key];
        const retainsDrag = previous?.event === 'drag' && ['update', 'autoschedule'].includes(change.event);
        const retainsAutoSchedule = previous?.event === 'autoschedule' && change.event === 'update';
        this.changeVersion++;
        if (change.event === 'delete' && previous?.event === 'add' && this.savingChanges[key]?.event !== 'add') {
            // A new row deleted before its first save never reaches the server.
            delete this.changes[key];
        } else {
            this.changes[key] = {
                ...change,
                _uuid: row._uuid,
                // Native follow-up notifications must retain the initiating edit's strategy.
                planningDirection: previous && (retainsDrag || retainsAutoSchedule) ? previous.planningDirection : this.backwardPlanningEnabled ? 'backward' : 'forward',
                task: change.entity === 'task' ? (row as GanttTask) : null,
                link: change.entity === 'link' ? (row as GanttLink) : change.link,
                // Editing a pending insert must still insert it on the server.
                event: previous?.event === 'add' && change.event !== 'delete' ? 'add' : retainsDrag ? 'drag' : retainsAutoSchedule ? 'autoschedule' : change.event,
            };
        }
        this.queueResourceGraphRefresh();
        if (this.options.save.autoSave) this.save$.next();
    }

    public save(): Promise<GanttSaveResponse> {
        if (this.saveInFlight) return this.saveInFlight;
        if (!this.hasChanges || !this.gantt) return Promise.resolve({ errorcode: 0, errormessage: '' });
        if (!this.options.dataTask?.loadData) {
            this.showSaveError('Gantt saving requires a source DataTask.', 'gantt_save_source_required');
            return Promise.reject(new Error(this.saveError));
        }
        if (this.planningDrag) {
            this.showSaveError('Finish moving the task before saving.', 'planning_save_drag_active');
            return Promise.reject(new Error(this.saveError));
        }
        if (this.planningSaveRequiresReload) {
            this.showSaveError('The planning data must be reloaded before saving again. Your edits have been kept.', 'planning_save_reload_required');
            return Promise.reject(new Error(this.saveError));
        }
        this.saving = true;
        this.saveError = '';
        let succeeded = false;
        this.saveInFlight = this.persistChanges()
            .then((response) => {
                succeeded = true;
                return response;
            })
            .finally(() => {
                this.saving = false;
                this.saveInFlight = undefined;
                if (succeeded && !this.destroyed && this.options.save.autoSave && this.hasChanges) this.save$.next();
            });
        return this.saveInFlight;
    }

    private async persistChanges(): Promise<GanttSaveResponse> {
        let response: GanttSaveResponse = { errorcode: 0, errormessage: '' };
        try {
            do {
                const pending = { ...this.changes };
                const changes = Object.values(pending).map((change) => this.toSaveChange(change));
                const planningIds = new Set(this.planningChanges);
                const planning = planningIds.size && this.planningConstraints?.save_mode === 'validated-plan' ? createPlanningSave(this.planningConstraints, planningIds) : undefined;
                this.savingChanges = pending;
                try {
                    response = await this.ganttService.saveGanttData({ loadDataTask: this.options.dataTask!.loadData, changes, ...(planning ? { planning } : {}) });
                    if (planning) {
                        this.planningConstraints = { ...this.planningConstraints!, snapshot_id: response.savedata!.planning!.snapshot_id };
                        for (const id of planningIds) this.planningChanges.delete(id);
                    }
                    for (const [key, change] of Object.entries(pending)) {
                        const latest = this.changes[key];
                        if (latest === change) delete this.changes[key];
                        // Edits made while an insert was in flight now update that
                        // inserted row, retaining its frontend UUID until refresh.
                        else if (change.event === 'add' && latest?.event === 'add') this.changes[key] = { ...latest, event: 'update' };
                    }
                } catch (error) {
                    for (const [key, change] of Object.entries(pending)) {
                        if (change.event === 'add' && this.changes[key]?.event === 'delete') delete this.changes[key];
                    }
                    throw error;
                } finally {
                    this.savingChanges = {};
                }
            } while (!this.destroyed && this.options.save.autoSave && this.hasChanges);
        } catch (error) {
            if (error instanceof GanttSaveError && error.requiresReload) this.planningSaveRequiresReload = true;
            this.showSaveError(error instanceof Error ? error.message : String(error), error instanceof GanttSaveError ? error.messageKey : undefined);
            throw error;
        }
        // Do not overwrite edits made while a manual save was in flight.
        if (!this.destroyed && !this.hasChanges && (this.options.save.readAfterSave || response.read === true || response.read === 1)) {
            await this.loadGantt();
        }
        return response;
    }

    private toSaveChange(change: GanttPendingChange): GanttSaveChange {
        const row = this.getChangeRow(change);
        const uuid = change._uuid;
        if (uuid === undefined || !row || String(row._uuid) !== String(uuid)) throw new Error('Gantt row _uuid cannot change before refresh.');
        const formatDate = this.gantt!.date.date_to_str('%Y-%m-%d %H:%i:%s');
        return {
            _uuid: uuid,
            id: change.id,
            entity: change.entity,
            event: change.event,
            parentId: change.parentId,
            planningDirection: change.planningDirection,
            row: {
                ...Object.fromEntries(
                    Object.entries(row)
                        .filter(([key]) => !key.startsWith('$'))
                        .map(([key, value]) => [key, this.planningConstraints ? this.serializePlanningValue(value) : value instanceof Date ? formatDate(value) : value]),
                ),
                _uuid: uuid,
            },
        };
    }

    private serializePlanningValue(value: unknown): unknown {
        if (value instanceof Date) return planningDateFromDate(value, this.planningConstraints!.time_basis).replace(/\.000$/, '');
        if (Array.isArray(value)) return value.map((item) => this.serializePlanningValue(item));
        if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.serializePlanningValue(item)]));
        return value;
    }

    private getChangeRow(change: GanttTaskChangeInfo): GanttTask | GanttLink | null {
        if (change.entity === 'task') return this.gantt?.isTaskExists(change.id) ? this.gantt.getTask(change.id) : change.task;
        return this.gantt?.isLinkExists(change.id) ? (this.gantt.getLink(change.id) as GanttLink) : change.link;
    }

    public reloadFromToolbar(): void {
        this.read().catch((error) => this.errorHandler.handleError(error));
    }
    public saveFromToolbar(): void {
        this.save().catch((error) => this.errorHandler.handleError(error));
    }

    public setFieldNames(enabled: boolean): void {
        if (!this.gantt || this.loading || this.saving || this.showFieldNames === enabled) return;
        this.syncColumnsFromNativeConfig();
        this.showFieldNames = enabled;
        this.configureResourceConfig(this.gantt);
        this.refreshColumnLayout();
    }

    public setBackwardPlanning(enabled: boolean): void {
        if (!this.gantt || this.loading || this.saving || this.backwardPlanningEnabled === enabled) return;
        const gantt = this.gantt;
        const scroll = gantt.getScrollState();
        const previousBackwardPlanning = this.backwardPlanningEnabled;
        this.viewOverrides.backwardPlanning = enabled;
        this.applyPlanningDirection(enabled);
        this.applyNativeConfig(gantt);
        if (enabled && this.options.config.project_end == null) gantt.config.project_end = undefined;
        this.configureAutoSchedulingProjectDates(gantt);
        this.updateInferredPlanningConstraints(previousBackwardPlanning, enabled);
        // Selecting a strategy preserves dates; subsequent edits use that strategy.
        gantt.render();
        gantt.scrollTo(scroll.x, scroll.y);
        this.rememberViewState();
    }

    private updateInferredPlanningConstraints(previousBackwardPlanning: boolean, enabled: boolean): void {
        if (!this.gantt || previousBackwardPlanning === enabled) return;
        const previousDefault = previousBackwardPlanning ? 'alap' : 'asap';
        for (const id of this.inferredConstraintTaskIds) {
            if (!this.gantt.isTaskExists(id)) continue;
            const task = this.gantt.getTask(id);
            if (task.constraint_type === previousDefault && !task.constraint_date) {
                task.constraint_type = enabled ? 'alap' : 'asap';
            }
        }
    }

    private applyPlanningDirection(enabled: boolean): void {
        this.options.autoScheduling.scheduleFromEnd = enabled;
        const config = this.options.autoScheduling.config;
        if (config && typeof config === 'object') {
            this.options.autoScheduling.config = { ...config, schedule_from_end: enabled };
        }
    }

    public setCriticalPath(enabled: boolean): void {
        if (!this.gantt || this.loading || this.saving) return;
        this.viewOverrides.criticalPath = enabled;
        this.criticalPath = enabled;
        this.options.config.highlight_critical_path = enabled;
        this.gantt.config.highlight_critical_path = enabled;
        this.gantt.render();
        this.rememberViewState();
    }

    public setResourceGraph(enabled: boolean): void {
        if (!this.gantt || this.loading || this.saving) return;
        const scroll = this.gantt.getScrollState();
        this.viewOverrides.resourceGraph = enabled;
        this.resourceEnabled = enabled;
        this.options.resources.enabled = enabled;
        this.configureResourceConfig(this.gantt);
        this.configureLayout(this.gantt);
        this.gantt.resetLayout();
        this.syncResourceStore(this.gantt);
        this.floatingFilters?.refresh();
        this.gantt.render();
        this.gantt.scrollTo(scroll.x, scroll.y);
        this.rememberViewState();
    }

    private rememberViewState(): void {
        this.rememberedState = this.captureRememberedState();
        this.rememberCurrentState();
    }

    public setColumnVisibility(propertyKey: string, visible: boolean): void {
        const column = this.toolbarColumns.find((item) => getColumnPropertyKey(item) === propertyKey);
        if (!column || (!visible && this.toolbarColumns.filter((item) => !this.isColumnHidden(item)).length <= 1 && !this.isColumnHidden(column))) return;
        this.syncColumnsFromNativeConfig();
        this.columnVisibility[propertyKey] = visible;
        this.options.columns = this.options.columns.map((item) => (getColumnPropertyKey(item) === propertyKey ? { ...item, hidden: !visible, hide: !visible } : item));
        this.refreshColumnLayout();
    }

    public reorderColumns(propertyKeys: string[]): void {
        this.syncColumnsFromNativeConfig();
        const byKey = new Map(this.toolbarColumns.map((column) => [getColumnPropertyKey(column), column]));
        const ordered = [...new Set(propertyKeys)].map((key) => byKey.get(key)).filter((column): column is GanttColumn => !!column);
        const included = new Set(ordered.map(getColumnPropertyKey));
        ordered.push(...this.toolbarColumns.filter((column) => !included.has(getColumnPropertyKey(column))));
        this.options.columns = this.options.columns.map((column) => (isGanttConfigurationColumn(getColumnPropertyKey(column)) ? column : ordered.shift()!));
        this.refreshColumnLayout();
    }

    public resetColumns(): void {
        this.columnVisibility = {};
        this.options.columns = mergeGanttOptions(this.backendOptions, this.ttOptions).columns.map((column) => ({ ...column }));
        this.refreshColumnLayout();
    }

    public setResourceColumnVisibility(propertyKey: string, visible: boolean): void {
        const column = this.toolbarResourceColumns.find((item) => getColumnPropertyKey(item) === propertyKey);
        if (!column || (!visible && this.toolbarResourceColumns.filter((item) => !this.isColumnHidden(item)).length <= 1 && !this.isColumnHidden(column))) return;
        this.syncColumnsFromNativeConfig();
        this.resourceColumnVisibility[propertyKey] = visible;
        this.options.resources.columns = this.options.resources.columns.map((item) => (getColumnPropertyKey(item) === propertyKey ? { ...item, hidden: !visible, hide: !visible } : item));
        this.refreshColumnLayout();
    }

    public reorderResourceColumns(propertyKeys: string[]): void {
        this.syncColumnsFromNativeConfig();
        const byKey = new Map(this.toolbarResourceColumns.map((column) => [getColumnPropertyKey(column), column]));
        const ordered = [...new Set(propertyKeys)].map((key) => byKey.get(key)).filter((column): column is GanttColumn => !!column);
        const included = new Set(ordered.map(getColumnPropertyKey));
        ordered.push(...this.toolbarResourceColumns.filter((column) => !included.has(getColumnPropertyKey(column))));
        this.options.resources.columns = this.options.resources.columns.map((column) => (isGanttConfigurationColumn(getColumnPropertyKey(column)) ? column : ordered.shift()!));
        this.refreshColumnLayout();
    }

    public resetResourceColumns(): void {
        this.resourceColumnVisibility = {};
        this.options.resources.columns = mergeGanttOptions(this.backendOptions, this.ttOptions).resources.columns.map((column) => ({ ...column }));
        this.refreshColumnLayout();
    }

    private refreshColumnLayout(): void {
        if (!this.gantt) return;
        const scroll = this.gantt.getScrollState();
        this.configureColumns(this.gantt);
        this.configureResourceConfig(this.gantt);
        this.configureLayout(this.gantt);
        this.gantt.resetLayout();
        this.syncResourceStore(this.gantt);
        this.floatingFilters?.refresh();
        this.gantt.render();
        this.gantt.scrollTo(scroll.x, scroll.y);
        this.rememberedState = this.captureRememberedState();
        this.rememberCurrentState();
    }

    private async getGanttConfiguration(loadSchema: boolean, version: number): Promise<GanttSetup | undefined> {
        const source = this.ttOptions?.dataTask;
        if (!source) return { data: [], links: [], options: {} };

        const request = resolveGanttDataTaskRequest(source.loadData);
        const options = loadSchema ? await this.ganttService.getGanttColumns({ loadDataMethod: request.method, parameters: request.parameters }) : (this.backendOptions ?? {});
        // Closing the view or starting a newer read must not launch another
        // expensive request when an older schema response eventually arrives.
        if (this.destroyed || version !== this.readVersion) return;
        const tasks = await this.ganttService.getGanttData(request);
        return { data: tasks.data, links: tasks.links, resources: tasks.resources, constraints: tasks.constraints, options };
    }

    private getPlanningLoad(snapshot: PlanningConstraints): PlanningLoadIndex {
        if (this.planningLoad?.snapshot !== snapshot) this.planningLoad = { snapshot, index: new PlanningLoadIndex(snapshot, this.planningLoad?.index) };
        return this.planningLoad.index;
    }

    private previewPlanningDrag(id: string | number, mode: string): void {
        if (!this.gantt || !this.planningDrag || mode !== this.gantt.config.drag_mode.move) return;
        const drag = this.planningDrag;
        const task = this.gantt.getTask(id);
        const direction = this.backwardPlanningEnabled ? 'backward' : 'forward';
        const date = this.toGanttDate(direction === 'backward' ? task.end_date : task.start_date);
        if (!date) return;
        const request: PlanningMove = { task_id: drag.taskId, direction, anchor_date: planningDateFromDate(date, drag.source.time_basis) };
        if (drag.request?.direction === request.direction && drag.request.anchor_date === request.anchor_date) {
            // Native dragging restores its elapsed-duration proposal on each
            // pointer event. Keep the allocated shape even when the date repeats.
            this.applyPlanningSnapshot(drag.result?.snapshot ?? drag.source, drag.rowUuids, true);
            return;
        }
        drag.request = request;
        const result = drag.engine.plan(request);
        if (!result.ok) {
            drag.result = undefined;
            drag.error = result.message;
            this.showSaveError(result.message, result.messageKey, result.messageParams);
            this.applyPlanningSnapshot(drag.source, drag.rowUuids, true);
            return;
        }
        drag.result = result;
        drag.error = undefined;
        this.saveError = '';
        drag.rowUuids = new Set(result.assignments.map((assignment) => assignment.row_uuid));
        this.applyPlanningSnapshot(result.snapshot, drag.rowUuids, true);
    }

    private findPlanningRow(uuid: string): GanttTask | undefined {
        const id = this.planningRowIds.get(uuid);
        return id !== undefined && this.gantt?.isTaskExists(id) ? this.gantt.getTask(id) : undefined;
    }

    private applyPlanningSnapshot(snapshot: PlanningConstraints, rowUuids: Set<string>, preview = false): void {
        if (!this.gantt) return;
        const gantt = this.gantt;
        this.applyingPlanning = true;
        try {
            gantt.batchUpdate(() => {
                for (const assignment of snapshot.assignments.filter((item) => rowUuids.has(item.row_uuid))) {
                    const task = this.findPlanningRow(assignment.row_uuid);
                    if (!task || task.id === undefined || assignment.start_date === null || assignment.end_date === null) continue;
                    task.start_date = planningDateToDate(assignment.start_date, snapshot.time_basis);
                    task.end_date = planningDateToDate(assignment.end_date, snapshot.time_basis);
                    task.duration = gantt.calculateDuration(task.start_date, task.end_date);
                    task.planned_segments = snapshot.reservations
                        .filter((booking) => booking.assignment_id === assignment.id)
                        .map((booking) => ({
                            start_date: planningDateToDate(booking.start_date, snapshot.time_basis),
                            end_date: planningDateToDate(booking.end_date, snapshot.time_basis),
                            hours_planned: (booking.effort_units * snapshot.unit_seconds) / 3600,
                            slot_id: booking.slot_id,
                        }));
                    this.captureResourceBookingBaseline(task);
                    gantt.updateTask(task.id);
                }
            }, preview);
            // A full render per pointer event repeatedly rebuilds the scales and
            // resource graph. Update only the affected bars while dragging.
            if (preview)
                for (const uuid of rowUuids) {
                    const task = this.findPlanningRow(uuid);
                    if (task?.id !== undefined) {
                        gantt.refreshTask(task.id);
                        if (task.parent && gantt.isTaskExists(task.parent)) gantt.refreshTask(task.parent);
                    }
                }
        } finally {
            this.applyingPlanning = false;
        }
        this.queueResourceGraphRefresh();
    }

    private normalizeTaskDates(tasks: GanttTask[]): GanttTask[] {
        return tasks.map((task) => {
            if (typeof task !== 'object' || task === null) {
                return task;
            }

            return {
                ...task,
                start_date: this.toGanttDate(task['start_date']) ?? task['start_date'],
                end_date: this.toGanttDate(task['end_date']) ?? task['end_date'],
            };
        });
    }

    private resolveResources(resources: MachineResource[] | undefined, tasks: GanttTask[]): MachineResource[] {
        const resolvedResources = resources?.length ? resources : this.buildResourcesFromTasks(tasks);

        return resolvedResources.map((resource) => {
            const namedResource = resource as MachineResource & Record<string, unknown>;

            return {
                ...resource,
                text: String(namedResource['text'] ?? namedResource['label'] ?? namedResource['name'] ?? namedResource['key'] ?? resource.id ?? ''),
            };
        });
    }

    private enableResourcePanelForAvailableResources(resources: MachineResource[]): void {
        if (!resources.length || this.hasExplicitResourceEnabledSetting()) return;

        this.options = {
            ...this.options,
            resources: {
                ...this.options.resources,
                enabled: true,
            },
        };
        this.resourceEnabled = true;
    }

    private hasExplicitResourceEnabledSetting(): boolean {
        return this.hasResourceEnabledSetting(this.backendOptions) || this.hasResourceEnabledSetting(this.ttOptions);
    }

    private hasResourceEnabledSetting(options: PartialGanttOptions | undefined): boolean {
        return !!options?.resources && Object.prototype.hasOwnProperty.call(options.resources, 'enabled');
    }

    private syncResourceStore(gantt: GanttStatic): void {
        const store = gantt.getDatastore(gantt.config.resource_store);
        if (!store?.parse) return;

        const rows = this.resources.map((resource) => this.toResourceStoreRow(resource, gantt));
        this.floatingFilters?.bindResources(store);
        store.clearAll();
        store.parse(rows);
        this.applyResourceSort(gantt);
        store.refresh();

        this.syncResourceSum(gantt);
    }

    private getGraphResources(): MachineResource[] {
        return this.resources.filter(resource => this.floatingFilters?.isResourceVisible(resource.id) ?? true);
    }

    private syncResourceSum(gantt: GanttStatic): void {
        const rows = this.getGraphResources().map(resource => this.toResourceStoreRow(resource, gantt));
        const sumStore = gantt.getDatastore('resourceSum');
        sumStore.clearAll();
        if (rows.length && this.options.resources.showSum) {
            const resourceIds = new Set(rows.map((resource) => String(resource.id)));
            let id = 'tt-gantt-resource-sum';
            while (resourceIds.has(id)) id += '_';
            // Parent resources describe groups; their child capacity already contributes.
            const parentIds = new Set(rows.map((resource) => String(resource['parent'])));
            const capacities = rows.filter((resource) => !parentIds.has(String(resource.id))).map((resource) => this.getResourceCapacity(resource));
            // A partial capacity total cannot establish whether the sum is overloaded.
            const capacity = capacities.every((value) => value !== undefined) ? capacities.reduce<number>((sum, value) => sum + value!, 0) : undefined;
            sumStore.parse([this.toResourceStoreRow({ id, text: 'Sum', capacity, isGanttResourceSum: true }, gantt)]);
        }
        sumStore.refresh();
    }

    private toResourceStoreRow(resource: MachineResource & Record<string, unknown>, gantt: GanttStatic): MachineResource & Record<string, unknown> {
        const text = this.getResourceDisplayName(resource);

        return {
            ...resource,
            parent: resource['parent'] ?? gantt.config.root_id,
            open: resource['open'] ?? true,
            text,
            label: resource['label'] ?? text,
            name: resource['name'] ?? text,
            resource: resource['resource'] ?? text,
        };
    }

    private normalizeResourceBackedTaskOwners(tasks: GanttTask[], resources: MachineResource[]): GanttTask[] {
        const resourceIds = new Set(resources.map((resource) => String(resource.id)));

        return tasks.map((task) => {
            if (typeof task !== 'object' || task === null || task.id === undefined || task['owner'] !== undefined) {
                return task;
            }

            const owner = task['equipment_id'] ?? task.parent ?? task.id;
            return resourceIds.has(String(owner)) ? { ...task, owner } : task;
        });
    }

    private buildResourcesFromTasks(tasks: GanttTask[]): MachineResource[] {
        return tasks
            .filter((task) => task['render'] === 'split')
            .map((task) => ({
                id: task.id!,
                text: task.text ?? String(task.id),
                capacity: this.getResourceCapacity({ id: task.id!, text: task.text ?? String(task.id), capacity: task.capacity ?? task.capacityHoursPerDay }),
            }));
    }

    private sortGridColumn(propertyKey: string, area: 'task' | 'resource', additive = false): void {
        if (!this.gantt || !this.isSortingEnabled()) return;
        const columns = area === 'task' ? this.options.columns : this.getVisibleResourceColumns();
        const column = columns.find((item) => getColumnPropertyKey(item) === propertyKey);
        if (!column || !isGanttColumnSortable(column)) return;
        const current = (area === 'task' ? this.taskSort : this.resourceSort).filter(sort => this.getSortColumn(sort, columns));
        const sort = cycleGanttSort(current, propertyKey, additive);
        this.sortInteracted = true;
        if (area === 'task') {
            this.taskSort = sort;
            if (sort.length) this.applyTaskSort(this.gantt, true);
            else {
                // Restore loaded sibling order without changing hierarchy, dates or selection.
                this.gantt.sort(createGanttSortComparator([], this.options.columns, this.loadedTaskOrder), false, undefined, true);
            }
        } else {
            this.resourceSort = sort;
            if (sort.length) this.applyResourceSort(this.gantt);
            else {
                const loadedOrder = new Map(this.resources.map((resource, index) => [String(resource.id), index]));
                this.gantt.getDatastore(this.gantt.config.resource_store)?.sort(createGanttSortComparator([], this.options.resources.columns, loadedOrder));
            }
            this.gantt.getDatastore(this.gantt.config.resource_store)?.refresh();
        }
        this.gantt.render();
        this.rememberViewState();
    }

    private isSortingEnabled(): boolean {
        return customCoerceBooleanProperty(this.options.config.sort ?? true);
    }

    private getSortColumn(sort: GanttSortState | undefined, columns: GanttColumn[]): GanttColumn | undefined {
        if (!this.isSortingEnabled() || !sort || !['asc', 'desc'].includes(sort.direction)) return undefined;
        const column = columns.find((item) => getColumnPropertyKey(item) === sort.propertyKey);
        return column && isGanttColumnSortable(column) ? column : undefined;
    }

    private applyTaskSort(gantt: GanttStatic, silent = false): void {
        if (!this.isSortingEnabled() || !this.taskSort.length) return;
        // Native tree sorting orders siblings recursively without changing parents or dates.
        gantt.sort(createGanttSortComparator(this.taskSort, this.options.columns, this.loadedTaskOrder), false, undefined, silent);
    }

    private applyResourceSort(gantt: GanttStatic): void {
        if (!this.isSortingEnabled() || !this.resourceSort.length) return;
        const loadedOrder = new Map(this.resources.map((resource, index) => [String(resource.id), index]));
        gantt.getDatastore(gantt.config.resource_store)?.sort(createGanttSortComparator(this.resourceSort, this.options.resources.columns, loadedOrder));
    }

    private observeGridHeaders(): void {
        if (!this.gantt || this.destroyed) return;
        // Native layout resizing can replace a grid header without onGanttRender.
        this.gridHeaderObserver ??= new MutationObserver(() => {
            if (this.destroyed) return;
            this.updateSortIndicators();
            this.floatingFilters?.render();
            this.rememberColumnStateIfChanged();
        });
        this.gridHeaderObserver.disconnect();
        for (const scale of [this.gantt.$grid_scale, this.gantt.getLayoutView('resourceGrid')?.$grid_scale]) {
            // Watch only native header replacement, not our changes inside each cell.
            if (scale) this.gridHeaderObserver.observe(scale, { childList: true });
        }
    }

    private updateSortIndicators(): void {
        if (!this.gantt) return;
        const grids = [
            { element: this.gantt.$grid_scale, sort: this.taskSort, columns: this.options.columns },
            { element: this.gantt.getLayoutView('resourceGrid')?.$grid_scale as HTMLElement | undefined, sort: this.resourceSort, columns: this.options.resources.columns },
        ];
        for (const grid of grids) {
            const sorts = grid.sort.filter(sort => this.getSortColumn(sort, grid.columns));
            for (const header of Array.from(grid.element?.querySelectorAll<HTMLElement>('.gantt_grid_head_cell') ?? [])) {
                header.querySelectorAll('.gantt_sort, .tt-gantt-sort-priority').forEach((icon) => icon.remove());
                header.removeAttribute('data-sort-priority');
                const key = header.getAttribute('data-column-name');
                const column = grid.columns.find((item) => getColumnPropertyKey(item) === key);
                const alignment = column?.align ?? 'left';
                header.style.textAlign = alignment;
                header.style.justifyContent = alignment === 'right' ? 'flex-end' : alignment === 'center' ? 'center' : 'flex-start';
                if (!column || !this.isSortingEnabled() || !isGanttColumnSortable(column)) {
                    header.removeAttribute('aria-sort');
                    continue;
                }
                const index = sorts.findIndex(sort => sort.propertyKey === key);
                // aria-sort identifies the primary column; visible numbers identify secondary priorities.
                header.setAttribute('aria-sort', index === 0 ? (sorts[index].direction === 'asc' ? 'ascending' : 'descending') : 'none');
                if (index >= 0) {
                    const icon = document.createElement('div');
                    icon.className = 'gantt_sort gantt_' + sorts[index].direction;
                    icon.setAttribute('aria-hidden', 'true');
                    if (sorts.length > 1) {
                        const priority = document.createElement('span');
                        priority.className = 'tt-gantt-sort-priority';
                        priority.textContent = String(index + 1);
                        header.setAttribute('data-sort-priority', priority.textContent);
                        header.appendChild(priority);
                    }
                    header.appendChild(icon);
                }
            }
        }
    }

    private toGanttGridColumn(column: GanttColumn): DhtmlxGanttConfigOptions['columns'][number] {
        const propertyKey = getColumnPropertyKey(column);

        return {
            name: propertyKey,
            label: this.showFieldNames ? propertyKey : (column.label ?? propertyKey),
            tree: customCoerceBooleanProperty(column.tree ?? propertyKey === 'text'),
            width: column.width ?? '*',
            min_width: column.minWidth ?? column['min_width'],
            max_width: column.maxWidth ?? column['max_width'],
            align: column.align,
            resize: customCoerceBooleanProperty(column.resize ?? true),
            sort: isGanttColumnSortable(column),
            onrender: (task: GanttTask, cell: HTMLElement) => {
                const value = task[propertyKey];
                cell.classList.toggle('tt-gantt-cell-action', !!(column.clickType || column.doubleClickType) && value != null && value !== '');
            },
            template: (task: GanttTask) => {
                const value = task[propertyKey];
                // Preserve an explicitly configured native date format for untyped date columns.
                if (value instanceof Date && !column.coltype && !column.dateFormat && this.options.config.date_grid && this.gantt) {
                    return escapeGanttColumnText(this.gantt.date.date_to_str(this.options.config.date_grid)(value));
                }
                return escapeGanttColumnText(formatGanttColumnValue(value, column));
            },
        };
    }

    private toGanttResourceGridColumn(column: GanttColumn): DhtmlxGanttConfigOptions['columns'][number] {
        const propertyKey = getColumnPropertyKey(column);

        return {
            ...this.toGanttGridColumn(column),
            onrender: (resource: MachineResource, cell: HTMLElement) => {
                const value = resource[propertyKey];
                cell.classList.toggle('tt-gantt-cell-action', !resource.isGanttResourceSum && !!(column.clickType || column.doubleClickType) && value != null && value !== '');
            },
            template: (resource: MachineResource & Record<string, unknown>) => {
                if (resource.isGanttResourceSum) return getColumnPropertyKey(this.getVisibleResourceColumns()[0]) === propertyKey ? this.getResourceDisplayName(resource) : '';
                const value = resource[propertyKey] ?? (this.isResourceNameColumn(propertyKey) ? this.getResourceDisplayName(resource) : undefined);
                return escapeGanttColumnText(formatGanttColumnValue(value, column));
            },
        };
    }

    private getNativeResourceColumns(): DhtmlxGanttConfigOptions['columns'] {
        const columns = this.getVisibleResourceColumns();

        return columns.map((column) => this.toGanttResourceGridColumn(column));
    }

    private getVisibleResourceColumns(): GanttColumn[] {
        const columns = this.options.resources.columns.map((column) => {
            if (!this.isResourceNameColumn(getColumnPropertyKey(column))) return column;

            return {
                ...column,
                width: this.getUsableResourceColumnWidth(column.width),
                tree: column.tree ?? false,
            };
        });

        const visibleColumns = columns.filter((column) => !this.isColumnHidden(column));

        return visibleColumns.length ? visibleColumns : [this.createResourceNameColumn()];
    }

    private createResourceNameColumn(): GanttColumn {
        return { label: 'Resource', propertyKey: 'text', tree: false, width: '*', resize: true };
    }

    private getUsableResourceColumnWidth(width: GanttColumn['width']): GanttColumn['width'] {
        if (typeof width === 'number' && width < 40) return '*';
        if (typeof width === 'string' && Number(width) < 40) return '*';

        return width ?? '*';
    }

    private isResourceNameColumn(propertyKey: string): boolean {
        return ['text', 'name', 'label', 'resource'].includes(propertyKey);
    }

    private getResourceDisplayName(resource: MachineResource & Record<string, unknown>): string {
        return String(resource['text'] ?? resource['label'] ?? resource['name'] ?? resource['key'] ?? resource.id ?? '');
    }

    private getColumnPropertyKey(column: GanttColumn): string {
        return getColumnPropertyKey(column);
    }

    private isColumnHidden(column: GanttColumn): boolean {
        return customCoerceBooleanProperty(column.hidden ?? column.hide ?? false);
    }

    private inferColumnsFromTasks(tasks: GanttTask[]): GanttColumn[] {
        const propertyKeys = new Set(tasks.flatMap((task) => Object.keys(task)));
        const columns = Array.from(propertyKeys)
            .filter((key) => !key.startsWith('$'))
            .map((key) => ({
                propertyKey: key,
                label: key,
                hidden: isGanttConfigurationColumn(key),
                tree: key === 'text',
                width: key === 'text' ? '*' : 110,
                resize: true,
            }));
        return columns.length ? columns : [this.createFallbackTextColumn()];
    }

    private createFallbackTextColumn(): GanttColumn {
        return { label: 'Tittel', propertyKey: 'text', tree: true, width: '*', resize: true };
    }

    private applyNativeConfig(gantt: GanttStatic, preserveZoom = true): void {
        const config = toNativeGanttConfig(this.options);

        // Zoom updates the task chart and resource timeline configurations together.
        // Reapplying schema defaults to only the main chart breaks date alignment
        // and can leave smart-rendered scale labels outside the viewport.
        if (preserveZoom) {
            config.scales = gantt.config.scales;
            config.min_column_width = gantt.config.min_column_width;
            config.scale_height = gantt.config.scale_height;
        }

        // Restore the native defaults before applying the current provider's options.
        if (this.planningNativeConfig) Object.assign(gantt.config, this.planningNativeConfig);
        Object.assign(gantt.config, config);
        if (this.planningConstraints) {
            this.planningNativeConfig = { work_time: gantt.config.work_time, correct_work_time: gantt.config.correct_work_time, auto_scheduling_initial: gantt.config.auto_scheduling_initial };
            // The shared planner owns allocation and dependencies for this provider.
            gantt.config.auto_scheduling = false;
            gantt.config.auto_scheduling_initial = false;
            gantt.config.work_time = false;
            gantt.config.correct_work_time = false;
        } else this.planningNativeConfig = undefined;

        gantt.config['highlight_critical_path'] = this.criticalPath;
        gantt.config['multiselect'] = this.multiSelect;
    }

    private refreshTimelineViewport(gantt: GanttStatic): void {
        const { x } = gantt.getScrollState();
        // The native repaint restores the DOM scroll position after rendering
        // smart scales at zero. Scroll each view to refresh its visible labels
        // and task layers, including when the scrollbar position is unchanged.
        for (const id of ['timeline', 'resourceHistogram', 'resourceSumHistogram']) {
            gantt.getLayoutView(id)?.scrollTo(x);
        }
    }

    private configureAutoSchedulingProjectDates(gantt: GanttStatic, tasks?: GanttTask[]): void {
        gantt.config.project_start = this.toGanttDate(gantt.config.project_start);
        gantt.config.project_end = this.toGanttDate(gantt.config.project_end);

        if (!gantt.config.schedule_from_end || gantt.config.project_end) return;

        const latestEndDate = this.getLatestTaskEndDate(tasks);

        if (latestEndDate) {
            gantt.config.project_end = latestEndDate;
        }
    }

    private getLatestTaskEndDate(tasks?: GanttTask[]): Date | undefined {
        const endDates: Date[] = [];

        if (tasks) {
            tasks.forEach((task) => {
                const date = this.toGanttDate(task.end_date);

                if (date) {
                    endDates.push(date);
                }
            });
        } else if (this.gantt) {
            this.gantt.eachTask((task: GanttTask) => {
                const date = this.toGanttDate(task.end_date);

                if (date) {
                    endDates.push(date);
                }
            });
        }

        return endDates.length ? new Date(Math.max(...endDates.map((date) => date.getTime()))) : undefined;
    }

    private toGanttDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;

        if (typeof value === 'string') {
            return this.parseGanttDateString(value);
        }

        return undefined;
    }

    private parseGanttDateString(value: string): Date | undefined {
        const trimmed = value.trim();
        if (!trimmed) return undefined;

        const isoLikeDate = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/);
        if (isoLikeDate) {
            return this.createValidDate(isoLikeDate[1], isoLikeDate[2], isoLikeDate[3], isoLikeDate[4], isoLikeDate[5], isoLikeDate[6]);
        }

        const dayMonthYearDate = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/);
        if (dayMonthYearDate) {
            return this.createValidDate(dayMonthYearDate[3], dayMonthYearDate[2], dayMonthYearDate[1], dayMonthYearDate[4], dayMonthYearDate[5], dayMonthYearDate[6]);
        }

        const date = new Date(trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T'));

        return isNaN(date.getTime()) ? undefined : date;
    }

    private createValidDate(yearValue: string, monthValue: string, dayValue: string, hourValue = '0', minuteValue = '0', secondValue = '0'): Date | undefined {
        const year = Number(yearValue);
        const month = Number(monthValue);
        const day = Number(dayValue);
        const hour = Number(hourValue);
        const minute = Number(minuteValue);
        const second = Number(secondValue);

        const date = new Date(year, month - 1, day, hour, minute, second);
        const isValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day && date.getHours() === hour && date.getMinutes() === minute && date.getSeconds() === second;

        return isValid ? date : undefined;
    }

    private getResourceCapacity(resource: MachineResource): number | undefined {
        if (resource.capacity == null || String(resource.capacity).trim() === '') return undefined;
        const capacity = Number(resource.capacity);
        return Number.isFinite(capacity) && capacity >= 0 ? capacity : undefined;
    }

    private getResourceGraphTasks(resource: MachineResource, tasks: GanttTask[]): GanttTask[] {
        const tasksById = new Map<string | number, GanttTask>();

        tasks.forEach((task) => {
            if (typeof task !== 'object' || task === null || task.id === undefined) return;
            if (this.isProjectLikeTask(task) && this.hasAssignedDescendant(task, resource.id)) return;
            tasksById.set(task.id, task);
        });

        if (!this.gantt || resource.id === undefined) {
            return Array.from(tasksById.values());
        }

        this.gantt.eachTask((task: GanttTask) => {
            if (typeof task !== 'object' || task === null || task.id === undefined) return;
            if (!this.isTaskAssignedToResource(task, resource.id)) return;
            if (this.isProjectLikeTask(task) && !this.shouldIncludeProjectInResourceGraph(task, resource.id)) return;

            tasksById.set(task.id, task);
        });

        return Array.from(tasksById.values());
    }

    private shouldIncludeProjectInResourceGraph(task: GanttTask, resourceId: string | number): boolean {
        if (!this.isProjectLikeTask(task) || !this.isTaskAssignedToResource(task, resourceId)) {
            return false;
        }

        return !this.hasAssignedDescendant(task, resourceId);
    }

    private isProjectLikeTask(task: GanttTask): boolean {
        const projectType = this.gantt?.config.types?.project ?? 'project';

        return task['type'] === projectType || task['render'] === 'split' || !!(this.gantt && task.id !== undefined && this.gantt.hasChild(task.id));
    }

    private hasAssignedDescendant(task: GanttTask, resourceId: string | number): boolean {
        if (!this.gantt || task.id === undefined || !this.gantt.hasChild(task.id)) {
            return false;
        }

        let hasAssignedDescendant = false;

        this.gantt.eachTask((descendant: GanttTask) => {
            if (descendant.id !== task.id && this.isTaskAssignedToResource(descendant, resourceId)) {
                hasAssignedDescendant = true;
            }
        }, task.id);

        return hasAssignedDescendant;
    }

    private isTaskAssignedToResource(task: GanttTask, resourceId: string | number): boolean {
        const owner = task['owner'];
        const resourceKey = String(resourceId);
        const getAssignmentResourceId = (assignment: unknown): unknown => (typeof assignment === 'object' && assignment !== null && 'resource_id' in assignment ? assignment['resource_id'] : assignment);

        if (Array.isArray(owner)) {
            return owner.some((assignment) => String(getAssignmentResourceId(assignment)) === resourceKey);
        }

        return String(getAssignmentResourceId(owner)) === resourceKey;
    }

    private resolveCriticalPathEnabled(): boolean {
        return this.options.config['highlight_critical_path'] ?? this.options.config.criticalPath ?? false;
    }

    private resolveMultiSelectEnabled(): boolean {
        return this.options.config['multiselect'] ?? this.options.config.multiSelect ?? false;
    }

    private resolveLinksEditable(): boolean {
        return this.options.editing.links && !customCoerceBooleanProperty(this.options.config.readonly ?? false);
    }

    private resolveResourcesEnabled(): boolean {
        return this.options.resources.enabled;
    }

    private getGanttResourcesConfig(): DhtmlxGanttConfigOptions['resources'] {
        return this.resolveResourcesEnabled() ? (this.options.resources.config ?? true) : false;
    }

    private resolveOptions(preserveRuntimeOptions = false): void {
        this.options = preserveRuntimeOptions ? mergeGanttOptions(this.options, this.backendOptions, this.ttOptions) : mergeGanttOptions(this.backendOptions, this.ttOptions);

        this.criticalPath = this.resolveCriticalPathEnabled();
        this.multiSelect = this.resolveMultiSelectEnabled();
        this.linksEditable = this.resolveLinksEditable();
        this.resourceEnabled = this.resolveResourcesEnabled();
    }

    private applyRememberedState(state: GanttRememberState | undefined, refreshGantt = false): void {
        if (!isGanttRememberState(state)) return;

        const previousBackwardPlanning = this.backwardPlanningEnabled;
        this.applyingRememberedState = true;
        // A late server preference must not undo a path selected or cleared on this visit.
        if (this.dependencyPathInteracted) state = { ...state, dependencyPathRootUuid: this.selectedPathRootUuid };
        else if (state.dependencyPathRootUuid === null || typeof state.dependencyPathRootUuid === 'string') {
            this.selectedPathRootUuid = state.dependencyPathRootUuid;
        }
        if (this.sortInteracted) state = { ...state, sort: rememberGanttSort(this.taskSort), resourceSort: rememberGanttSort(this.resourceSort) };
        else {
            this.taskSort = normalizeGanttSort(state.sort);
            this.resourceSort = normalizeGanttSort(state.resourceSort);
        }
        this.rememberedState = state;
        this.columnVisibility = { ...state.columns?.visibility };
        this.resourceColumnVisibility = { ...state.resourceColumns?.visibility };
        this.viewOverrides = { ...state.view };
        if (typeof state.view?.criticalPath === 'boolean') {
            this.criticalPath = state.view.criticalPath;
            this.options.config.highlight_critical_path = this.criticalPath;
        }
        if (typeof state.view?.resourceGraph === 'boolean') {
            this.resourceEnabled = state.view.resourceGraph;
            this.options.resources.enabled = this.resourceEnabled;
        }

        if (typeof state.view?.backwardPlanning === 'boolean') {
            this.applyPlanningDirection(state.view.backwardPlanning);
        }

        this.options.columns = applyRememberedColumnState(this.options.columns, state.columns);
        this.options.resources = {
            ...this.options.resources,
            columns: applyRememberedColumnState(this.options.resources.columns, state.resourceColumns),
        };

        if (state.gridWidth !== undefined) {
            this.options.config.grid_width = state.gridWidth;
        }

        if (refreshGantt && this.gantt) {
            this.configureColumns(this.gantt);
            this.applyNativeConfig(this.gantt);
            this.configureAutoSchedulingProjectDates(this.gantt);
            this.updateInferredPlanningConstraints(previousBackwardPlanning, this.backwardPlanningEnabled);
            this.configureResourceConfig(this.gantt);
            this.configureLayout(this.gantt);
            this.gantt.resetLayout();
            this.syncResourceStore(this.gantt);
            this.floatingFilters?.refresh();
            this.applyTaskSort(this.gantt);
            this.applyRememberedZoomLevel(this.gantt, state.zoomLevel);
            this.restoreDependencyPathHighlight();
            this.restoreRememberedTimelinePosition(this.gantt, state.timelineCenterDate);
        }

        if (this.gantt && !refreshGantt) {
            this.applyRememberedZoomLevel(this.gantt, state.zoomLevel);
            this.restoreRememberedTimelinePosition(this.gantt, state.timelineCenterDate);
        }

        this.lastRememberedStateJson = JSON.stringify(state);
        this.applyingRememberedState = false;
    }

    private applyRememberedZoomLevel(gantt: GanttStatic, zoomLevel: GanttRememberState['zoomLevel']): void {
        if (zoomLevel === undefined) return;

        try {
            gantt.ext.zoom.setLevel(normalizeGanttZoomLevel(zoomLevel, gantt.ext.zoom.getLevels()));
        } catch (error) {
            console.error(error);
        }
    }

    private rememberColumnStateIfChanged(): void {
        if (!this.gantt || this.applyingRememberedState) return;

        const signature = this.getNativeColumnSignature(this.gantt);

        if (!this.lastNativeColumnSignature) {
            this.lastNativeColumnSignature = signature;
            return;
        }

        if (signature !== this.lastNativeColumnSignature) {
            this.lastNativeColumnSignature = signature;
            this.rememberState$.next();
        }
    }

    private getNativeColumnSignature(gantt: GanttStatic): string {
        return JSON.stringify((gantt.config.columns ?? []).map((column) => ({ name: column.name, width: column.width })));
    }

    private getConfiguredColumnsSignature(columns: GanttColumn[]): string {
        return JSON.stringify(columns.map((column) => ({ propertyKey: getColumnPropertyKey(column), width: column.width, hidden: column.hidden ?? column.hide })));
    }

    private rememberCurrentState(): void {
        if (!this.ttRememberId || !this.gantt) return;

        const state = this.captureRememberedState();
        const stateJson = JSON.stringify(state);

        if (stateJson === this.lastRememberedStateJson) return;

        this.rememberedState = state;
        this.lastRememberedStateJson = stateJson;
        this.rememberCurrentStateLocal(state);
        this.remember.remember(this.ttRememberId, state).catch((error) => console.error(error));
    }

    private captureRememberedState(): GanttRememberState {
        this.syncColumnsFromNativeConfig();

        return createGanttRememberState({
            columns: createRememberedColumnState(this.options.columns, this.columnVisibility),
            resourceColumns: createRememberedColumnState(this.options.resources.columns, this.resourceColumnVisibility),
            ...(Object.keys(this.viewOverrides).length ? { view: { ...this.viewOverrides } } : {}),
            zoomLevel: this.gantt ? getCurrentGanttZoomLevel(this.gantt) : undefined,
            timelineCenterDate: this.captureTimelineCenterDate(),
            dependencyPathRootUuid: this.selectedPathRootUuid,
            sort: rememberGanttSort(this.taskSort),
            resourceSort: rememberGanttSort(this.resourceSort),
            gridWidth: typeof this.gantt?.config.grid_width === 'number' ? this.gantt.config.grid_width : undefined,
            sidePanel: this.rememberedState?.sidePanel,
        });
    }

    private syncColumnsFromNativeConfig(): void {
        if (!this.gantt) return;

        this.options.columns = this.mergeNativeColumnStateIntoColumns(this.options.columns, this.gantt.config.columns ?? []);
        const resourceColumns = this.gantt.getLayoutView('resourceGrid')?.$getConfig().columns;
        if (resourceColumns) this.options.resources.columns = this.mergeNativeColumnStateIntoColumns(this.options.resources.columns, resourceColumns);
    }

    private mergeNativeColumnStateIntoColumns(columns: GanttColumn[], nativeColumns: DhtmlxGanttConfigOptions['columns']): GanttColumn[] {
        const columnsByKey = new Map(columns.map((column) => [getColumnPropertyKey(column), column]));
        const nativeColumnKeys = nativeColumns.map((column) => column.name).filter((name): name is string => !!name);
        const orderedVisibleColumns = nativeColumnKeys.reduce<GanttColumn[]>((result, propertyKey) => {
            const column = columnsByKey.get(propertyKey);
            const nativeColumn = nativeColumns.find((item) => item.name === propertyKey);

            if (column) {
                result.push({
                    ...column,
                    width: nativeColumn?.width ?? column.width,
                });
            }

            return result;
        }, []);

        const visibleColumnKeys = new Set(orderedVisibleColumns.map(getColumnPropertyKey));
        // Retain hidden columns in their saved positions when syncing visible widths/order.
        return columns.map((column) => (visibleColumnKeys.has(getColumnPropertyKey(column)) ? orderedVisibleColumns.shift()! : column));
    }

    private captureTimelineCenterDate(): string | undefined {
        if (!this.gantt) return undefined;

        try {
            const scrollState = this.gantt.getScrollState();
            const timelineWidth = this.gantt.$task?.clientWidth ?? 0;
            const centerPosition = (scrollState?.x ?? 0) + timelineWidth / 2;
            const centerDate = this.gantt.dateFromPos(centerPosition);

            return centerDate instanceof Date && !isNaN(centerDate.getTime()) ? centerDate.toISOString() : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    private restoreRememberedTimelinePosition(gantt: GanttStatic, timelineCenterDate: string | undefined): void {
        if (!timelineCenterDate) return;

        try {
            const date = new Date(timelineCenterDate);

            if (isNaN(date.getTime())) return;

            const timelineWidth = gantt.$task?.clientWidth ?? 0;
            this.timelineRange?.showDate(date);
            const scrollLeft = Math.max(0, gantt.posFromDate(date) - timelineWidth / 2);

            gantt.scrollTo(scrollLeft, null);
        } catch (error) {
            console.error(error);
        }
    }

    private getLastRememberedStateLocal(): GanttRememberState | undefined {
        if (!this.ttRememberId) return undefined;

        const localStorageKey = this.getLocalStorageKey();
        if (!localStorageKey) return undefined;

        try {
            const allStates = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
            const state = allStates[this.ttRememberId];

            return isGanttRememberState(state) ? state : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    private rememberCurrentStateLocal(state: GanttRememberState): void {
        const localStorageKey = this.getLocalStorageKey();
        if (!localStorageKey) return;

        try {
            const allStates = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
            allStates[this.ttRememberId] = state;
            localStorage.setItem(localStorageKey, JSON.stringify(allStates));
        } catch (error) {
            console.error(error);
        }
    }

    private async getLastRememberedState(): Promise<GanttRememberState | undefined> {
        if (!this.ttRememberId) return undefined;

        try {
            const state = await this.remember.getLastStatus(this.ttRememberId, true);

            return isGanttRememberState(state) ? state : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    private getLocalStorageKey(): string | undefined {
        const userKey = this.appSettings.settings.user?.portalUserKeynoEdit;

        return userKey ? `tt-gantt.${userKey}` : undefined;
    }
}

interface GanttPendingChange extends GanttTaskChangeInfo {
    planningDirection: GanttPlanningDirection;
}

interface GanttBookingBaseline {
    start: number;
    end: number;
}

interface GanttTaskClick {
    area: GanttNavigationArea;
    id: string | number;
    newTab: boolean;
    column?: GanttColumn;
}

interface GanttPlanningDrag {
    source: PlanningConstraints;
    engine: PlanningEngine;
    taskId: string;
    rowUuids: Set<string>;
    request?: PlanningMove;
    result?: PlanningSuccess;
    error?: string;
}
